import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Wallet,
  TrendingUp,
  TrendingDown,
  LayoutGrid,
  List,
  Target,
  Check,
  PieChart,
  Camera,
  Star,
  Sparkles,
  Info,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { Holding, MarketSignal, WatchlistItem, AutoTradeLog, MembershipTier } from '../types';
import { formatCurrency } from '../utils/market';
import { getAssetMarket } from '../hooks/usePortfolioData';
import { syncAllBrokerages } from '../services/api';
import { PortfolioScraper } from './PortfolioScraper';
import { PortfolioTimeSeriesChart } from './PortfolioTimeSeriesChart';

const COMMON_NAMES: Record<string, string> = {
  'AAPL': '애플',
  'TSLA': '테슬라',
  'NVDA': '엔비디아',
  'MSFT': '마이크로소프트',
  'AMZN': '아마존',
  'GOOG': '구글',
  'GOOGL': '구글',
  'META': '메타',
  'NFLX': '넷플릭스',
  'AMD': 'AMD',
  'AVGO': '브로드컴',
  'INTC': '인텔',
  'QCOM': '퀄컴',
  'COIN': '코인베이스',
  'PLTR': '팔란티어',
  'SPY': 'S&P 500 ETF',
  'QQQ': '나스닥 100 ETF',
  'TLT': '미국 20년+ 국채 ETF',
  'GLD': '금 ETF (SPDR Gold)',
  'SLV': '은 ETF (iShares Silver)',
  'IAU': '금 ETF (iShares Gold)',
  'SOXX': '필라델피아 반도체 ETF',
  'SOXL': '필라델피아 반도체 3배 레버리지 ETF',
  'TQQQ': '나스닥 100 3배 레버리지 ETF',
  'TSLL': '테슬라 2배 레버리지 ETF',
  'NVDL': '엔비디아 2배 레버리지 ETF',
  'SCHD': 'Schwab 미국 배당 다우존스 ETF',
  'IVV': 'iShares S&P 500 ETF',
  'VOO': 'Vanguard S&P 500 ETF',
  'DIA': '다우존스 30 ETF',
  'IWM': '러셀 2000 ETF',
  'VTI': '뱅가드 전체주식시장 ETF',
  '005930.KS': '삼성전자',
  '005930': '삼성전자',
  '000660.KS': 'SK하이닉스',
  '000660': 'SK하이닉스',
  '035420.KS': 'NAVER',
  '035420': 'NAVER',
  '035720.KS': '카카오',
  '035720': '카카오',
  '005380.KS': '현대차',
  '005380': '현대차',
  '000270.KS': '기아',
  '000270': '기아',
};

export const getHoldingDisplayName = (
  holding: { symbol: string; name?: string },
  signal?: { name?: string },
  lang: string = 'ko'
): string => {
  const sym = holding.symbol.toUpperCase();
  const isCash = ['CASH', 'USD', 'KRW'].includes(sym);
  if (isCash) {
    return lang === 'ko' ? '현금 자산' : 'CASH';
  }

  if (holding.name && holding.name.trim().toUpperCase() !== sym) {
    return holding.name;
  }
  if (signal?.name && signal.name.trim().toUpperCase() !== sym) {
    return signal.name;
  }
  if (COMMON_NAMES[sym]) {
    return COMMON_NAMES[sym];
  }
  return holding.name || holding.symbol;
};

export const getHoldingSubText = (
  holding: { symbol: string; name?: string },
  signal?: { name?: string },
  lang: string = 'ko'
): string => {
  const sym = holding.symbol.toUpperCase();
  const isCash = ['CASH', 'USD', 'KRW'].includes(sym);
  if (isCash) {
    return lang === 'ko' ? '현금 자산' : 'CASH';
  }

  const displayName = getHoldingDisplayName(holding, signal, lang);
  if (displayName.trim().toUpperCase() !== sym) {
    return holding.symbol;
  }
  return lang === 'ko' ? '주식 자산' : 'EQUITY';
};

export interface BrokerMeta {
  name: string;
  shortName: string;
  icon: string;
  color: string;
  badgeBg: string;
  barColor: string;
}

export const getBrokerInfo = (providerOrId?: string, accountsList: any[] = []): BrokerMeta => {
  const raw = (providerOrId || '').toLowerCase();
  const matchedAcc = accountsList.find(a => a.id === providerOrId || a.account_num === providerOrId);
  const key = (matchedAcc ? matchedAcc.provider : raw).toLowerCase();

  if (key.includes('kiwoom')) {
    return {
      name: '키움증권',
      shortName: '키움',
      icon: '🔴',
      color: 'text-rose-400',
      badgeBg: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
      barColor: 'bg-rose-500'
    };
  }
  if (key.includes('koreainvest') || key.includes('kis')) {
    return {
      name: '한국투자증권',
      shortName: '한투',
      icon: '🟢',
      color: 'text-emerald-400',
      badgeBg: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
      barColor: 'bg-emerald-500'
    };
  }
  if (key.includes('ls')) {
    return {
      name: 'LS증권',
      shortName: 'LS',
      icon: '🔵',
      color: 'text-blue-400',
      badgeBg: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
      barColor: 'bg-blue-500'
    };
  }
  if (key.includes('mirae')) {
    return {
      name: '미래에셋증권',
      shortName: '미래',
      icon: '🟠',
      color: 'text-orange-400',
      badgeBg: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
      barColor: 'bg-orange-500'
    };
  }
  if (key.includes('samsung')) {
    return {
      name: '삼성증권',
      shortName: '삼성',
      icon: '🔷',
      color: 'text-sky-400',
      badgeBg: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
      barColor: 'bg-sky-500'
    };
  }
  if (key.includes('toss')) {
    return {
      name: '토스증권',
      shortName: '토스',
      icon: '⚡',
      color: 'text-blue-400',
      badgeBg: 'bg-blue-600/15 text-blue-300 border-blue-500/30',
      barColor: 'bg-blue-600'
    };
  }
  if (key.includes('kb')) {
    return {
      name: 'KB증권',
      shortName: 'KB',
      icon: '🟡',
      color: 'text-amber-400',
      badgeBg: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
      barColor: 'bg-amber-500'
    };
  }
  if (key.includes('shinhan')) {
    return {
      name: '신한투자증권',
      shortName: '신한',
      icon: '🔷',
      color: 'text-cyan-400',
      badgeBg: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
      barColor: 'bg-cyan-500'
    };
  }
  if (key.includes('nh') || key.includes('namuh')) {
    return {
      name: 'NH투자증권',
      shortName: 'NH',
      icon: '🌿',
      color: 'text-emerald-400',
      badgeBg: 'bg-emerald-600/15 text-emerald-300 border-emerald-600/30',
      barColor: 'bg-emerald-600'
    };
  }
  if (key.includes('manual')) {
    return {
      name: '수동 기입',
      shortName: '수동',
      icon: '✏️',
      color: 'text-slate-400',
      badgeBg: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
      barColor: 'bg-slate-500'
    };
  }
  return {
    name: matchedAcc?.account_name || '연동 계좌',
    shortName: matchedAcc?.provider ? matchedAcc.provider.toUpperCase() : '연동',
    icon: '🏦',
    color: 'text-indigo-400',
    badgeBg: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
    barColor: 'bg-indigo-500'
  };
};

export interface HoldingAccountDistItem {
  accountId: string;
  accountName: string;
  accountNum?: string;
  shares: number;
  pct: number;
  meta: BrokerMeta;
}

export const getHoldingAccountDistributions = (
  holding: Holding,
  accountsList: any[] = []
): HoldingAccountDistItem[] => {
  const totalShares = holding.shares || 0;

  if (holding.distribution && holding.distribution.length > 0) {
    const list = holding.distribution
      .filter(d => Number(d.shares || 0) > 0)
      .map(d => {
        const acc = accountsList.find(a => a.id === d.accountId);
        const meta = getBrokerInfo(acc ? acc.provider : d.accountId, accountsList);
        const shares = Number(d.shares || 0);
        const pct = totalShares > 0 ? (shares / totalShares) * 100 : 0;
        return {
          accountId: d.accountId,
          accountName: acc?.account_name || meta.name,
          accountNum: acc?.account_num,
          shares,
          pct,
          meta
        };
      })
      .sort((a, b) => b.shares - a.shares);

    if (list.length > 0) return list;
  }

  if (holding.account_id) {
    const acc = accountsList.find(a => a.id === holding.account_id);
    const meta = getBrokerInfo(acc ? acc.provider : holding.account_id, accountsList);
    return [{
      accountId: holding.account_id,
      accountName: acc?.account_name || meta.name,
      accountNum: acc?.account_num,
      shares: totalShares,
      pct: 100,
      meta
    }];
  }

  return [];
};

const getAlertColor = (level: string) => {
  switch (level) {
    case 'CRITICAL': return 'text-red-500 bg-red-500/10 border-red-500/20';
    case 'HIGH': return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
    case 'ELEVATED': return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
    case 'WATCH': return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
    default: return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
  }
};

interface PortfolioManagerProps {
  holdings: Holding[];
  signals: MarketSignal[];
  initialSimulateSymbol?: string;
  onAdd: (mode?: 'stock' | 'cash') => void;
  onUpdate: (symbol: string, updates: Partial<Holding>, fee?: number) => void;
  onDelete: (symbol: string) => void;
  onLiquidate?: (symbol: string, sharesToClose: number, salePrice: number, fee?: number) => void;
  onSelectStock?: (symbol: string, type: 'fundamental' | 'financial' | 'quantitative') => void;
  language?: string;
  marketMode?: 'US' | 'KR' | 'ALL';
  onShowAllocation?: () => void;
  onBulkAdd?: (assets: Array<{ symbol: string, shares: number }>) => void;

  // Watchlist props
  watchlist?: WatchlistItem[];
  onAddWatchlist?: (symbol: string, name?: string, market?: string, notes?: string) => Promise<void>;
  onRemoveWatchlist?: (symbol: string, market?: string) => Promise<void>;
  onPromoteToHolding?: (symbol: string, shares: number, avgCost: number) => void;

  activeTab: 'holdings' | 'watchlist';
  onTabChange: (tab: 'holdings' | 'watchlist') => void;

  onSelectTechnicalStock?: (symbol: string) => void;
  isImportScreenshotEnabled?: boolean;
  isAddAssetEnabled?: boolean;
  isTechnicalAnalysisEnabled?: boolean;

  // Tier limit props (for inline guidance instead of upgrade modal)
  watchlistLimit?: number;
  holdingsLimit?: number;
  watchlistCount?: number;
  holdingsCount?: number;

  // Trade execution & notification props
  onSaveTradeExecution?: (trades: (Omit<AutoTradeLog, 'id'> & { id?: string })[]) => void;
  onShowToast?: (message: string, type: 'success' | 'error') => void;

  // Membership tier (for edit button visibility)
  membershipTier?: MembershipTier;

  // Currency & exchange rate
  currencyPreference?: 'USD' | 'KRW';
  exchangeRate?: number;
  totalValue?: number;

  // Account identification
  selectedAccountId?: string;
  accounts?: any[];

  // Live vs Paper Trading mode
  isLiveMode?: boolean;
  paperExecutionMode?: 'VIRTUAL_SANDBOX' | 'BROKER_PAPER';
}

export function PortfolioManager({
  holdings,
  signals,
  initialSimulateSymbol,
  onAdd,
  onUpdate,
  onDelete,
  onLiquidate,
  onSelectStock,
  language = 'ko',
  marketMode = 'US',
  onShowAllocation,
  onBulkAdd,
  watchlist = [],
  onAddWatchlist,
  onRemoveWatchlist,
  onPromoteToHolding,
  activeTab,
  onTabChange,
  onSelectTechnicalStock,
  isImportScreenshotEnabled = true,
  isAddAssetEnabled = true,
  isTechnicalAnalysisEnabled = true,
  watchlistLimit,
  holdingsLimit,
  watchlistCount,
  holdingsCount,
  onSaveTradeExecution,
  onShowToast,
  membershipTier,
  currencyPreference = 'USD',
  exchangeRate = 1380,
  totalValue: propTotalValue,
  selectedAccountId,
  accounts = [],
  isLiveMode = false,
  paperExecutionMode
}: PortfolioManagerProps) {
  const lang = language || 'ko';
  const market = marketMode || 'US';
  const isAll = market === 'ALL';
  const isUs = market === 'US' || isAll;
  const isKr = market === 'KR' || isAll;

  // Track paper execution mode for isolated UX (pure virtual sandbox vs broker paper VTS)
  const [currentPaperMode, setCurrentPaperMode] = useState<'VIRTUAL_SANDBOX' | 'BROKER_PAPER'>(
    paperExecutionMode || 'VIRTUAL_SANDBOX'
  );

  useEffect(() => {
    if (paperExecutionMode) {
      setCurrentPaperMode(paperExecutionMode);
    }
  }, [paperExecutionMode]);

  useEffect(() => {
    const syncConfig = async () => {
      try {
        const { fetchUserTradingConfig } = await import('../services/api');
        const cfg = await fetchUserTradingConfig();
        if (cfg?.paper_execution_mode) {
          setCurrentPaperMode(cfg.paper_execution_mode);
        }
      } catch {}
    };
    if (!paperExecutionMode) {
      syncConfig();
    }
    const handler = (e: any) => {
      if (e?.detail?.paperExecutionMode) {
        setCurrentPaperMode(e.detail.paperExecutionMode);
      } else {
        syncConfig();
      }
    };
    window.addEventListener('app:refresh', handler);
    window.addEventListener('trading-config:updated', handler);
    return () => {
      window.removeEventListener('app:refresh', handler);
      window.removeEventListener('trading-config:updated', handler);
    };
  }, [paperExecutionMode]);

  const isVirtualSandbox = !isLiveMode && (currentPaperMode === 'VIRTUAL_SANDBOX');

  // 실거래 모드에서만 증권사 계좌 연동 사용자의 수동 편집을 제한 (모의투자 모드에서는 가상 시뮬레이션을 위해 편집 허용)
  const isManualEditDisabled = isLiveMode && membershipTier !== undefined && membershipTier !== 'TIER1';
  const editDisabledTitle = lang === 'ko'
    ? '실거래 증권 계좌 연동 사용자는 포트폴리오 데이터가 자동 동기화됩니다'
    : 'Portfolio data is auto-synced for live brokerage-linked accounts';

  // 실거래 허용 모드에서는 멤버십 Tier 1 이용자만 보유자산 추가 가능 (나머지 등급은 증권 계좌 자동 연동)
  const canAddAsset = !isLiveMode || membershipTier === 'TIER1' || !membershipTier;

  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionHolding, setActionHolding] = useState<Holding | null>(null);
  const [actionType, setActionType] = useState<'adjust' | 'liquidate' | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');

  // Brokerage sync states
  const [isSyncingBrokerage, setIsSyncingBrokerage] = useState(false);
  const [syncCooldown, setSyncCooldown] = useState(0);

  const handleSyncBrokerageAccounts = async () => {
    if (syncCooldown > 0) return;
    setIsSyncingBrokerage(true);
    try {
      const res = await syncAllBrokerages({ force: true, isPaper: !isLiveMode, mode: isLiveMode ? 'live' : 'paper' });
      if (res && res.success) {
        onShowToast?.(
          lang === 'ko'
            ? (res.cached ? '최신 동기화 캐시 상태를 확인했습니다.' : '증권사 계좌 자산이 실시간 동기화되었습니다.')
            : 'Brokerage assets synchronized successfully.',
          'success'
        );
        setSyncCooldown(30);
        const timer = setInterval(() => {
          setSyncCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        window.dispatchEvent(new CustomEvent('app:refresh'));
      } else {
        onShowToast?.(
          lang === 'ko'
            ? `동기화 실패: ${res?.error || '알 수 없는 오류'}`
            : `Sync failed: ${res?.error || 'Unknown error'}`,
          'error'
        );
      }
    } catch (err: any) {
      onShowToast?.(
        lang === 'ko'
          ? `동기화 오류: ${err?.message || err}`
          : `Sync error: ${err?.message || err}`,
        'error'
      );
    } finally {
      setIsSyncingBrokerage(false);
    }
  };

  // Pagination states
  const [holdingsPage, setHoldingsPage] = useState(1);
  const [holdingsPageSize, setHoldingsPageSize] = useState(8);
  const [watchlistPage, setWatchlistPage] = useState(1);
  const [watchlistPageSize, setWatchlistPageSize] = useState(8);

  // Reset to page 1 when market mode or selected account changes
  React.useEffect(() => {
    setHoldingsPage(1);
    setWatchlistPage(1);
  }, [market, selectedAccountId]);

  // Watchlist & Simulator states
  const [quickAddSymbol, setQuickAddSymbol] = useState('');
  const [isQuickAdding, setIsQuickAdding] = useState(false);
  const [quickAddError, setQuickAddError] = useState<string | null>(null);
  const [simulatedShares, setSimulatedShares] = useState<Record<string, number>>({});
  const [simulatedPrices, setSimulatedPrices] = useState<Record<string, number>>({});
  const [expandedSimulation, setExpandedSimulation] = useState<Record<string, boolean>>({});

  // Auto-expand sizing simulator when initialSimulateSymbol changes
  React.useEffect(() => {
    if (initialSimulateSymbol) {
      onTabChange('watchlist');
      setExpandedSimulation({ [initialSimulateSymbol]: true });
      const matchingSignal = signals.find(s => s.symbol.toUpperCase() === initialSimulateSymbol.toUpperCase());
      const basePrice = (matchingSignal?.price && matchingSignal.price > 0) ? matchingSignal.price : 0;
      if (basePrice > 0) {
        setSimulatedPrices({ [initialSimulateSymbol]: basePrice });
      }
    }
  }, [initialSimulateSymbol, signals, onTabChange]);

  interface SearchResult {
    symbol: string;
    name: string;
    exchDisp: string;
    type: string;
  }

  const handleQuickAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickAddSymbol.trim() || !onAddWatchlist) return;

    const sym = quickAddSymbol.trim();
    const upperSym = sym.toUpperCase();

    // ── Minimum input length guard ───────────────────────────────────────
    // Require at least 2 US ticker chars, 2 KR name chars, or a full 6-digit code
    const isExactCode = /^\d{6}(\.KS|\.KQ)?$/.test(sym); // pure KR 6-digit
    const isExactTicker = /^[A-Z]{1,5}(\.KS|\.KQ|\.US)?$/.test(upperSym); // upper-case alpha ticker
    if (!isExactCode && !isExactTicker && sym.length < 2) {
      setQuickAddError(
        lang === 'ko'
          ? '종목명 또는 티커를 2자 이상 입력해 주세요.'
          : 'Please enter at least 2 characters.'
      );
      return;
    }

    setIsQuickAdding(true);
    setQuickAddError(null);
    try {
      // Determine market from input characteristics
      const isKrLike =
        /[\uac00-\ud7a3]/.test(sym) ||
        /^\d{6}$/.test(sym) ||
        upperSym.endsWith('.KS') ||
        upperSym.endsWith('.KQ');

      let searchMarket: 'US' | 'KR' = market === 'ALL' ? (isKrLike ? 'KR' : 'US') : (market as 'US' | 'KR');

      // Call search API
      let searchUrl = `/api/stocks/search?q=${encodeURIComponent(sym)}&market=${searchMarket}`;
      let res = await fetch(searchUrl, { credentials: 'include' });
      if (!res.ok) throw new Error('Search request failed');
      const data = (await res.json()) as { results: SearchResult[] };
      let results = data.results || [];

      // Fallback search in opposite market if market is ALL and no results found
      if (results.length === 0 && market === 'ALL') {
        const alternateMarket = searchMarket === 'US' ? 'KR' : 'US';
        searchUrl = `/api/stocks/search?q=${encodeURIComponent(sym)}&market=${alternateMarket}`;
        res = await fetch(searchUrl, { credentials: 'include' });
        if (res.ok) {
          const alternateData = (await res.json()) as { results: SearchResult[] };
          if (alternateData.results && alternateData.results.length > 0) {
            results = alternateData.results;
            searchMarket = alternateMarket;
          }
        }
      }

      if (results.length === 0) {
        setQuickAddError(
          lang === 'ko'
            ? '존재하지 않거나 지원되지 않는 종목입니다.'
            : 'This stock does not exist or is not supported.'
        );
        return;
      }

      // ── Exact / near-exact match guard ───────────────────────────────────
      // Only auto-pick when the user typed a precise symbol or a very specific name.
      // Otherwise, multiple ambiguous results could silently pick the wrong stock.
      const inputUpper = upperSym.replace(/\.(KS|KQ)$/, ''); // strip suffix for comparison
      const exactSymbolMatch = results.find(
        r => r.symbol.toUpperCase().replace(/\.(KS|KQ)$/, '') === inputUpper
      );
      const exactNameMatch = results.find(
        r => r.name.toLowerCase() === sym.toLowerCase()
      );
      const bestMatch = exactSymbolMatch || exactNameMatch;

      if (!bestMatch) {
        // Ambiguous input: show candidates count but refuse to auto-add
        setQuickAddError(
          lang === 'ko'
            ? `"${sym}"에 대한 검색 결과가 ${results.length}개 있습니다. 정확한 종목 코드나 종목명을 입력해 주세요. (예: TSLA, 005930, 삼성전자)`
            : `Found ${results.length} results for "${sym}". Enter the exact ticker or company name (e.g. TSLA, 005930).`
        );
        return;
      }

      const finalSym = bestMatch.symbol;
      const finalName = bestMatch.name;
      const finalMarket = searchMarket;

      // Duplicate check
      const alreadyWatchlisted = watchlist.some(
        w => w.symbol.toUpperCase() === finalSym.toUpperCase() && w.market === finalMarket
      );
      if (alreadyWatchlisted) {
        setQuickAddError(
          lang === 'ko'
            ? `${finalSym} (${finalName})은(는) 이미 관심종목에 등록되어 있습니다.`
            : `${finalSym} (${finalName}) is already in the watchlist.`
        );
        return;
      }

      await onAddWatchlist(finalSym, finalName, finalMarket, '');
      setQuickAddSymbol('');
    } catch (err) {
      console.error('Failed to add to watchlist', err);
      setQuickAddError(
        lang === 'ko'
          ? '관심종목 추가 중 오류가 발생했습니다.'
          : 'An error occurred while adding to the watchlist.'
      );
    } finally {
      setIsQuickAdding(false);
    }
  };

  // ── 진행 상태를 PositionActionModal에 전달하기 위한 상태 ──
  const [processingState, setProcessingState] = useState<{
    isProcessing: boolean;
    stage: string;
    message: string;
    result?: 'success' | 'error';
    resultMessage?: string;
  }>({ isProcessing: false, stage: '', message: '' });

  const handleConfirmWithProgress = async (shares: number, priceOrAvgCost: number, fee: number) => {
    if (!actionHolding || !actionType) return;
    const symbol = actionHolding.symbol;
    const name = actionHolding.name || '';
    const market = actionHolding.market || (actionHolding.symbol.endsWith('.KS') || actionHolding.symbol.endsWith('.KQ') ? 'KR' : 'US');
    const currency = market === 'KR' ? 'KRW' : 'USD';

    try {
      // Stage 1: 검증
      setProcessingState({
        isProcessing: true,
        stage: 'validating',
        message: lang === 'ko' ? '거래 정보를 검증 중입니다...' : 'Validating transaction...'
      });
      await new Promise(r => setTimeout(r, 300)); // 최소한의 UX 딜레이

      // Stage 2: 실행
      setProcessingState({
        isProcessing: true,
        stage: 'executing',
        message: actionType === 'adjust'
          ? (lang === 'ko' ? `${symbol} 포지션을 수정 중입니다...` : `Updating ${symbol} position...`)
          : (lang === 'ko' ? `${symbol} ${shares}주를 청산 중입니다...` : `Liquidating ${shares} shares of ${symbol}...`)
      });

      if (actionType === 'adjust') {
        await onUpdate(symbol, { shares, avgCost: priceOrAvgCost }, fee);
      } else if (actionType === 'liquidate') {
        if (!onLiquidate) {
          throw new Error(lang === 'ko' ? '청산 함수가 정의되지 않았습니다.' : 'Liquidate function is not defined.');
        }
        await onLiquidate(symbol, shares, priceOrAvgCost, fee);
      }

      // Stage 3: 거래 내역 저장
      setProcessingState({
        isProcessing: true,
        stage: 'saving',
        message: lang === 'ko' ? '거래 내역을 저장 중입니다...' : 'Saving trade history...'
      });

      // P&L 계산 (청산 시에만 의미 있음)
      const costBasis = (actionHolding.avgCost || 0) * shares;
      const pnl = actionType === 'liquidate'
        ? (priceOrAvgCost - (actionHolding.avgCost || 0)) * shares - fee
        : 0;
      const pnlPct = actionType === 'liquidate' && costBasis > 0
        ? (pnl / costBasis) * 100
        : 0;

      const tradeLog: Omit<AutoTradeLog, 'id'> = {
        symbol,
        name,
        market,
        action: actionType === 'adjust' ? 'ADJUST' : 'SELL',
        triggerType: 'MANUAL',
        shares,
        executionPrice: priceOrAvgCost,
        totalAmount: priceOrAvgCost * shares,
        currency,
        status: 'FILLED',
        executedAt: new Date().toISOString(),
        pnl,
        pnlPct,
        notes: actionType === 'adjust'
          ? (lang === 'ko' ? `수동 포지션 수정 (avgCost: ${actionHolding.avgCost} → ${priceOrAvgCost}, shares: ${actionHolding.shares} → ${shares})` : `Manual position adjust (avgCost: ${actionHolding.avgCost} → ${priceOrAvgCost}, shares: ${actionHolding.shares} → ${shares})`)
          : (lang === 'ko' ? `수동 청산 (fee: ${fee})` : `Manual liquidation (fee: ${fee})`)
      };

      if (onSaveTradeExecution) {
        await onSaveTradeExecution([tradeLog]);
      }

      // Stage 4: 완료
      setProcessingState({
        isProcessing: false,
        stage: 'done',
        message: '',
        result: 'success',
        resultMessage: actionType === 'adjust'
          ? (lang === 'ko' ? `${symbol} 포지션이 성공적으로 수정되었습니다.` : `${symbol} position updated successfully.`)
          : (lang === 'ko' ? `${symbol} ${shares}주가 성공적으로 청산되었습니다.` : `${shares} shares of ${symbol} liquidated successfully.`)
      });

      if (onShowToast) {
        onShowToast(
          actionType === 'adjust'
            ? (lang === 'ko' ? `${symbol} 포지션 수정 완료` : `${symbol} position updated`)
            : (lang === 'ko' ? `${symbol} ${shares}주 청산 완료` : `${shares} shares of ${symbol} liquidated`),
          'success'
        );
      }

      // 1.5초 후 모달 닫기
      setTimeout(() => {
        setActionHolding(null);
        setActionType(null);
        setProcessingState({ isProcessing: false, stage: '', message: '' });
      }, 1500);

    } catch (e) {
      console.error(`[PortfolioManager] ${actionType} failed for ${symbol}:`, e);
      setProcessingState({
        isProcessing: false,
        stage: 'error',
        message: '',
        result: 'error',
        resultMessage: lang === 'ko'
          ? `${symbol} ${actionType === 'adjust' ? '수정' : '청산'} 중 오류가 발생했습니다. 다시 시도해 주세요.`
          : `Failed to ${actionType === 'adjust' ? 'update' : 'liquidate'} ${symbol}. Please try again.`
      });
      if (onShowToast) {
        onShowToast(
          lang === 'ko'
            ? `${symbol} ${actionType === 'adjust' ? '수정' : '청산'} 실패`
            : `${symbol} ${actionType === 'adjust' ? 'update' : 'liquidation'} failed`,
          'error'
        );
      }
    }
  };

  const [isDeletingCurrentAccount, setIsDeletingCurrentAccount] = useState(false);

  const handleDeleteCurrentAccount = async () => {
    if (!selectedAccountId || selectedAccountId === 'all') return;
    const currentAccount = accounts.find((a: any) => a.id === selectedAccountId);
    const isManual = selectedAccountId.startsWith('manual') || String(currentAccount?.provider).toLowerCase() === 'manual';
    const accName = currentAccount?.account_name || (isManual ? (lang === 'ko' ? '수동 기입 계좌' : 'Manual Account') : selectedAccountId);

    const confirmMsg = isManual
      ? (lang === 'ko'
          ? `[경고] "${accName}"와 이 계좌에 등록된 모든 수동 기입 보유 자산이 영구 삭제됩니다.\n정말 삭제하시겠습니까?`
          : `[Warning] "${accName}" and all associated manual holdings will be permanently deleted.\nAre you sure you want to proceed?`)
      : (lang === 'ko'
          ? `"${accName}" 연동을 해제하고 연계된 자산 내역을 삭제하시겠습니까?`
          : `Disconnect "${accName}" and delete associated holdings?`);

    if (!window.confirm(confirmMsg)) return;

    setIsDeletingCurrentAccount(true);
    try {
      const { deleteConnectedAccount } = await import('../services/api');
      await deleteConnectedAccount(selectedAccountId);
      if (onShowToast) {
        onShowToast(
          lang === 'ko'
            ? '계좌 및 관련 보유 자산이 성공적으로 삭제되었습니다.'
            : 'Account and holdings deleted successfully.',
          'success'
        );
      }
      window.dispatchEvent(new CustomEvent('app:refresh'));
    } catch (e: any) {
      console.error('[PortfolioManager] Failed to delete account:', e);
      if (onShowToast) {
        onShowToast(
          lang === 'ko' ? `계좌 삭제 실패: ${e?.message || e}` : `Failed to delete account: ${e?.message || e}`,
          'error'
        );
      }
    } finally {
      setIsDeletingCurrentAccount(false);
    }
  };

  const computedTotalValue = holdings.reduce((sum, h) => {
    const signal = signals.find(s => s.symbol.toUpperCase() === h.symbol.toUpperCase());
    const isCash = (h.symbol === 'CASH' || h.symbol === 'USD' || h.symbol === 'KRW');
    let price = isCash ? 1 : ((signal?.price && signal.price > 0) ? signal.price : h.avgCost);
    
    // Normalize price to currencyPreference if mixed
    const isKrStock = h.market === 'KR' || h.symbol.endsWith('.KS') || h.symbol.endsWith('.KQ');
    if (currencyPreference === 'KRW' && !isKrStock && h.symbol !== 'KRW') {
      price *= exchangeRate;
    } else if (currencyPreference === 'USD' && isKrStock) {
      price /= exchangeRate;
    }
    return sum + (price * h.shares);
  }, 0);

  const totalValue = (propTotalValue !== undefined && propTotalValue > 0) ? propTotalValue : computedTotalValue;

  const calculateReturn = (holding: Holding) => {
    const signal = signals.find(s => s.symbol.toUpperCase() === holding.symbol.toUpperCase());
    const isCash = holding.symbol === 'CASH' || holding.symbol === 'USD' || holding.symbol === 'KRW';
    const currentPrice = isCash ? 1 : (signal?.price && signal.price > 0 ? signal.price : holding.avgCost);

    // If currentPrice is 0 (syncing state), we assume return is 0 to prevent -100% display
    const returnPct = isCash ? 0 : (currentPrice === 0 ? 0 : ((currentPrice - holding.avgCost) / holding.avgCost) * 100);

    return {
      price: currentPrice,
      pct: isNaN(returnPct) ? 0 : returnPct,
      dailyPct: signal?.pctChange || 0
    };
  };

  // Sort holdings by weight
  const sortedHoldings = [...holdings].sort((a, b) => (b.weight || 0) - (a.weight || 0));

  // Filter watchlist by market mode
  const filteredWatchlist = watchlist.filter(item => {
    if (market === 'ALL') return true;
    return item.market === market;
  });

  // Pagination calculations and clamping
  const totalHoldingsPages = Math.max(1, Math.ceil(sortedHoldings.length / holdingsPageSize));
  React.useEffect(() => {
    if (holdingsPage > totalHoldingsPages) {
      setHoldingsPage(totalHoldingsPages);
    }
  }, [totalHoldingsPages, holdingsPage]);

  const totalWatchlistPages = Math.max(1, Math.ceil(filteredWatchlist.length / watchlistPageSize));
  React.useEffect(() => {
    if (watchlistPage > totalWatchlistPages) {
      setWatchlistPage(totalWatchlistPages);
    }
  }, [totalWatchlistPages, watchlistPage]);

  const paginatedHoldings = sortedHoldings.slice(
    (holdingsPage - 1) * holdingsPageSize,
    holdingsPage * holdingsPageSize
  );

  const paginatedWatchlist = filteredWatchlist.slice(
    (watchlistPage - 1) * watchlistPageSize,
    watchlistPage * watchlistPageSize
  );

  const getStockBeta = (symbol: string): number => {
    const sym = symbol.toUpperCase();
    if (sym.includes('SPY') || sym.includes('VOO') || sym.includes('IVV')) return 1.0;
    if (sym.includes('QQQ')) return 1.2;
    if (sym.includes('IWM')) return 1.15;
    if (sym.includes('NVDA') || sym.includes('TSLA') || sym.includes('AMD')) return 1.45;
    if (sym.includes('AAPL') || sym.includes('MSFT') || sym.includes('GOOG') || sym.includes('META')) return 1.15;
    if (sym.includes('AMZN') || sym.includes('NFLX')) return 1.2;
    if (sym.includes('005930.KS') || sym.includes('005935.KS')) return 0.95; // Samsung
    if (sym.includes('000660.KS')) return 1.25; // Hynix
    if (sym.includes('035420.KS') || sym.includes('035720.KS')) return 1.1; // Naver, Kakao
    if (sym.includes('005380.KS') || sym.includes('000270.KS')) return 0.85; // Hyundai, Kia
    if (sym.includes('CASH') || sym.includes('USD') || sym.includes('KRW')) return 0;
    return 1.1; // Fallback
  };

  const currentPortfolioBeta = holdings.reduce((sum, h) => {
    const isHgetCash = h.symbol === 'CASH' || h.symbol === 'USD' || h.symbol === 'KRW';
    const signal = signals.find(s => s.symbol.toUpperCase() === h.symbol.toUpperCase());
    const price = isHgetCash ? 1 : ((signal?.price && signal.price > 0) ? signal.price : h.avgCost);
    const val = h.shares * price;
    const weight = totalValue > 0 ? val / totalValue : 0;
    const beta = isHgetCash ? 0 : getStockBeta(h.symbol);
    return sum + (weight * beta);
  }, 0);

  const liquidCash = holdings.reduce((sum, h) => {
    const isHgetCash = h.symbol === 'CASH' || h.symbol === 'USD' || h.symbol === 'KRW';
    if (isHgetCash) {
      return sum + h.shares;
    }
    return sum;
  }, 0);

  const AnalysisLinks = ({ symbol, full = false }: { symbol: string; full?: boolean }) => (
    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelectTechnicalStock && onSelectTechnicalStock(symbol);
        }}
        className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tighter bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 transition-all active:scale-90"
        title={lang === 'ko' ? '기술적 분석 보기' : 'Technical Analysis'}
      >
        {full && lang === 'ko' ? '기술적' : 'TCH'}
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelectStock?.(symbol, 'fundamental');
        }}
        className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tighter bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-all active:scale-90"
        title={lang === 'ko' ? '펀더멘털 분석' : 'Fundamental Analysis'}
      >
        {full && lang === 'ko' ? '펀더멘털' : 'FND'}
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelectStock?.(symbol, 'financial');
        }}
        className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tighter bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all active:scale-90"
        title={lang === 'ko' ? '재무 분석' : 'Financial Analysis'}
      >
        {full && lang === 'ko' ? '재무' : 'FIN'}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col space-y-6">
      {/* Header with Actions and Toggle */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-purple-400" />
            <h3 className="text-base font-black text-white/95 tracking-tight uppercase">
              {lang === 'ko' ? '보유 자산 및 배분 현황' : 'HOLDINGS & ASSET ALLOCATION'}
            </h3>
          </div>
          <div className="flex flex-col gap-2 mt-2">
            <p className="text-white/40 text-[11px] font-medium">
              {lang === 'ko'
                ? (isLiveMode
                    ? '실거래 증권 계좌 보유 자산 현황 및 실시간 포트폴리오 비중 관리'
                    : '모의투자 시뮬레이션 보유 종목 현황 및 전술적 비중 관리')
                : 'Real-time asset management and target weight optimization'}
            </p>
            <div className="flex flex-wrap items-center gap-3">
            {/* Market Mode Badge */}
            <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border flex items-center gap-1.5 ${market === 'KR'
              ? 'bg-blue-500/10 border-blue-500/20 text-blue-400'
              : market === 'US'
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                : 'bg-purple-500/10 border-purple-500/20 text-purple-400'
              }`}>
              <span>{market === 'KR' ? '🇰🇷' : market === 'US' ? '🇺🇸' : '🌐'}</span>
              {market === 'KR'
                ? (lang === 'ko' ? 'KR 포트폴리오' : 'KR Portfolio')
                : market === 'US'
                  ? 'US Portfolio'
                  : (lang === 'ko' ? '통합 포트폴리오' : 'Global Portfolio')
              }
            </span>

            {/* Paper Execution Mode Badge (Virtual Sandbox vs Broker Paper) */}
            {!isLiveMode && (
              <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold border flex items-center gap-1 ${
                currentPaperMode === 'VIRTUAL_SANDBOX'
                  ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
                  : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
              }`}>
                <span>{currentPaperMode === 'VIRTUAL_SANDBOX' ? '🧪' : '🏦'}</span>
                <span>
                  {lang === 'ko'
                    ? (currentPaperMode === 'VIRTUAL_SANDBOX' ? '순수 가상 시드머니' : '증권사 모의투자 VTS')
                    : (currentPaperMode === 'VIRTUAL_SANDBOX' ? 'Virtual Sandbox' : 'Broker Paper VTS')}
                </span>
              </span>
            )}

            {/* Mode-specific actions next to the badge */}
            {activeTab === 'holdings' ? (
              <div className="flex items-center gap-2">
                {/* 원클릭 증권사 자산 동기화 버튼 (순수 가상 시드머니 활성화 시 숨김 처리) */}
                {!isVirtualSandbox && (
                  <button
                    type="button"
                    onClick={handleSyncBrokerageAccounts}
                    disabled={isSyncingBrokerage || syncCooldown > 0}
                    title={lang === 'ko'
                      ? (isLiveMode ? '연동된 실거래 증권사 계좌의 보유 자산과 예수금을 실시간으로 동기화합니다.' : '증권사 모의투자 가상 계좌의 잔고를 동기화합니다.')
                      : (isLiveMode ? 'Sync live holdings and cash from connected brokerages' : 'Sync paper holdings from connected brokerages')}
                    className="flex items-center justify-center gap-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white transition-all px-3.5 py-2 rounded-xl font-bold text-xs shadow-md shadow-emerald-950/30 active:scale-95 disabled:opacity-50 cursor-pointer border border-emerald-400/30"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isSyncingBrokerage ? 'animate-spin' : ''}`} />
                    {isSyncingBrokerage
                      ? (lang === 'ko' ? '동기화 중...' : 'Syncing...')
                      : syncCooldown > 0
                        ? (lang === 'ko' ? `${syncCooldown}초 대기` : `Wait ${syncCooldown}s`)
                        : (lang === 'ko' ? (isLiveMode ? '⚡ 실계좌 동기화' : '⚡ 모의 잔고 동기화') : '⚡ Sync Assets')}
                  </button>
                )}

                {onBulkAdd && isImportScreenshotEnabled && canAddAsset && (
                  <PortfolioScraper
                    onSuccess={onBulkAdd}
                    language={lang}
                  />
                )}
                {isAddAssetEnabled && canAddAsset && (
                  <button
                    onClick={() => onAdd('stock')}
                    className="flex items-center justify-center gap-2 bg-[#ffffff] text-black hover:bg-purple-400 hover:text-white transition-all px-4 py-2 rounded-xl font-bold uppercase text-xs tracking-wider shadow-md shadow-white/5 active:scale-95 border border-white/10"
                  >
                    <Plus className="w-4 h-4" />
                    {lang === 'ko' ? '자산 추가' : 'ADD ASSET'}
                  </button>
                )}
                  {selectedAccountId && selectedAccountId !== 'all' && (
                    <button
                      type="button"
                      onClick={handleDeleteCurrentAccount}
                      disabled={isDeletingCurrentAccount}
                      title={lang === 'ko' ? '현재 선택된 계좌 및 관련 자산 일괄 삭제' : 'Delete selected account and its holdings'}
                      className="flex items-center justify-center gap-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 transition-all px-3 py-2.5 rounded-2xl font-bold text-xs active:scale-95 disabled:opacity-50 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {isDeletingCurrentAccount
                        ? (lang === 'ko' ? '삭제 중...' : 'Deleting...')
                        : (lang === 'ko' ? '계좌 삭제' : 'DELETE ACCOUNT')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <form onSubmit={handleQuickAdd} className="flex gap-2 items-center">
                    <div className="relative w-48 md:w-64">
                      <Search className="w-3.5 h-3.5 absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                      <input
                        type="text"
                        placeholder={
                          market === 'KR'
                            ? (lang === 'ko' ? "빠른 한국 관심종목 추가..." : "Quick add KR stock...")
                            : market === 'US'
                              ? (lang === 'ko' ? "빠른 미국 관심종목 추가..." : "Quick add US stock...")
                              : (lang === 'ko' ? "빠른 관심종목 추가..." : "Quick add stock...")
                        }
                        value={quickAddSymbol}
                        onChange={(e) => {
                          setQuickAddSymbol(e.target.value);
                          if (quickAddError) setQuickAddError(null);
                        }}
                        className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-2xl text-[11px] focus:outline-none focus:border-amber-500/50 transition-all text-white outline-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isQuickAdding || !quickAddSymbol.trim()}
                      className="px-4 py-2.5 bg-white text-black hover:bg-amber-400 hover:text-black disabled:opacity-20 transition-all rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 flex items-center justify-center gap-1.5 shadow-xl shadow-white/5 border border-white/10"
                    >
                      {isQuickAdding ? (
                        <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          <span>{lang === 'ko' ? '추가' : 'ADD'}</span>
                        </>
                      )}
                    </button>
                  </form>
                  {quickAddError && (
                    <span className="text-red-400 text-xs ml-2 font-medium">{quickAddError}</span>
                  )}
                  {/* Watchlist limit exceeded inline guidance */}
                  {watchlistLimit !== undefined && watchlistLimit !== -1 && watchlistCount !== undefined && watchlistCount >= watchlistLimit && (
                    <span className="text-amber-400 text-[10px] ml-2 font-bold flex items-center gap-1">
                      <Info className="w-3 h-3 shrink-0" />
                      {lang === 'ko'
                        ? `관심종목 한도(${watchlistLimit}개)를 모두 사용했습니다. 등급 업그레이드가 필요합니다.`
                        : `Watchlist limit (${watchlistLimit}) reached. Upgrade required.`}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Tab Switcher: Full-width Segmented Control on Mobile, regular compact layout on Desktop */}
          <div className="glass-premium p-1 rounded-xl flex items-center gap-1 w-full md:w-auto grid grid-cols-2 md:flex">
            <button
              onClick={() => onTabChange('holdings')}
              className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${activeTab === 'holdings'
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                : 'text-white/40 hover:text-white hover:bg-white/5'
                }`}
            >
              <Wallet className={`w-4 h-4 transition-colors ${activeTab === 'holdings' ? 'text-white' : 'text-white/40'}`} />
              <span>{lang === 'ko' ? '보유자산' : 'Holdings'}</span>
            </button>
            <button
              onClick={() => onTabChange('watchlist')}
              className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${activeTab === 'watchlist'
                ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/30'
                : 'text-white/40 hover:text-white hover:bg-white/5'
                }`}
            >
              <Star className={`w-4 h-4 transition-colors ${activeTab === 'watchlist' ? 'text-black fill-black' : 'text-amber-400 fill-amber-400/20'}`} />
              <span>{lang === 'ko' ? '관심종목' : 'Watchlist'}</span>
            </button>
          </div>

          {/* View Toggle - for both holdings and watchlist */}
          {(activeTab === 'holdings' || activeTab === 'watchlist') && (
            <div className="glass-premium p-1 rounded-xl flex items-center gap-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/20' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/20' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1">
        {/* Mobile Context Guide Help Text */}
        <div className="block md:hidden px-6 py-2 -mt-2 mb-4 text-[10px] font-bold tracking-widest text-center uppercase">
          {activeTab === 'holdings' ? (
            <span className="text-purple-400 bg-purple-500/10 px-3 py-1 rounded-full border border-purple-500/20 flex items-center justify-center gap-1">
              <Wallet className="w-3.5 h-3.5" />
              {lang === 'ko' ? '실제 보유 중인 자산 목록' : 'ACTUAL PORTFOLIO HOLDINGS'}
            </span>
          ) : (
            <span className="text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20 flex items-center justify-center gap-1">
              <Star className="w-3.5 h-3.5 fill-amber-400" />
              {lang === 'ko' ? '모니터링 중인 관심종목 목록' : 'WATCHLIST MONITORING ITEMS'}
            </span>
          )}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'holdings' ? (
            <motion.div
              key="holdings"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {paginatedHoldings.map((holding, idx) => {
                    const { price, pct } = calculateReturn(holding);
                    const signal = signals.find(s => s.symbol.toUpperCase() === holding.symbol.toUpperCase());
                    const isCash = holding.symbol === 'CASH' || holding.symbol === 'USD' || holding.symbol === 'KRW';
                    const assetMarket = holding.market || (holding.symbol.endsWith('.KS') || holding.symbol.endsWith('.KQ') ? 'KR' : 'US');
                    const targetDisplayCurrency = holding.displayCurrency || (currencyPreference === 'KRW' ? 'KR' : 'US');
                    const targetDisplayValue = holding.displayValue !== undefined ? holding.displayValue : (holding.value || holding.shares * price);
                    const nativeValue = holding.value || holding.shares * price;
                    const hasDiffCurrency = assetMarket !== targetDisplayCurrency;
                    let displayName = getHoldingDisplayName(holding, signal, lang);
                    if (isAll && isCash) {
                      if (holding.symbol.toUpperCase() === 'USD') {
                        displayName = lang === 'ko' ? '현금 자산 (달러)' : 'USD Cash';
                      } else if (holding.symbol.toUpperCase() === 'KRW' || holding.symbol.toUpperCase() === 'CASH') {
                        displayName = lang === 'ko' ? '현금 자산 (원화)' : 'KRW Cash';
                      }
                    }
                    const subText = getHoldingSubText(holding, signal, lang);
                    const accountDists = getHoldingAccountDistributions(holding, accounts || []);
                    const isMultiAccount = accountDists.length > 1;
                    const primaryDist = accountDists[0];

                    return (
                      <motion.div
                        key={holding.symbol}
                        layoutId={holding.symbol}
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="glass-premium p-6 rounded-[2rem] relative overflow-hidden group premium-gradient-bg transition-all mb-2 cursor-default"
                      >
                        <div className="absolute inset-0 bg-purple-500/0 group-hover:bg-purple-500/5 transition-colors duration-300 pointer-events-none" />

                        <div className="flex items-start justify-between gap-3 mb-6 min-w-0">
                          <div className="flex items-center gap-3.5 min-w-0 flex-1">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl font-black text-white shadow-2xl border-2 transition-all duration-500 group-hover:scale-105 group-hover:rotate-3 flex-shrink-0 ${isCash
                              ? 'bg-gradient-to-br from-blue-600/30 to-indigo-600/10 border-blue-500/30'
                              : 'bg-gradient-to-br from-purple-600/30 to-blue-600/10 border-white/10 shadow-purple-500/10'
                              }`}>
                              {holding.symbol === 'CASH' || holding.symbol === 'KRW' ? '₩' :
                                holding.symbol === 'USD' ? '$' :
                                  displayName[0]}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <h3
                                  className={`font-black text-white tracking-tight group-hover:text-purple-400 transition-colors leading-snug break-words ${
                                    displayName.length > 24 ? 'text-sm sm:text-base' : displayName.length > 15 ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'
                                  }`}
                                  title={displayName}
                                >
                                  {displayName}
                                </h3>
                                <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-md flex-shrink-0 ${assetMarket === 'KR' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                                  }`}>
                                  {assetMarket}
                                </span>
                              </div>
                              <p className="text-xs font-bold text-white/40 mt-0.5 uppercase tracking-wider truncate">
                                {subText}
                              </p>
                            </div>
                          </div>

                          {/* Brokerage & Account Badge */}
                          {isMultiAccount ? (
                            <span
                              className="text-[9px] font-black px-2.5 py-1 bg-gradient-to-r from-purple-500/25 to-indigo-500/25 text-purple-200 border border-purple-500/40 rounded-full tracking-wide uppercase flex items-center gap-1.5 shrink-0 self-start mt-0.5 shadow-md shadow-purple-950/40 backdrop-blur-sm cursor-help transition-transform hover:scale-105"
                              title={accountDists.map(d => `${d.meta.name}: ${d.shares.toLocaleString()}주 (${d.pct.toFixed(0)}%)`).join('\n')}
                            >
                              <span className="text-[10px]">🌐</span>
                              <span className="font-extrabold">{lang === 'ko' ? `${accountDists.length}개사 분산` : `${accountDists.length} Brokers`}</span>
                            </span>
                          ) : primaryDist ? (
                            <span
                              className={`text-[9px] font-black px-2.5 py-1 rounded-full tracking-wide uppercase flex items-center gap-1.5 shrink-0 self-start mt-0.5 border shadow-md backdrop-blur-sm transition-transform hover:scale-105 ${primaryDist.meta.badgeBg}`}
                              title={primaryDist.accountName + (primaryDist.accountNum ? ` (${primaryDist.accountNum})` : '')}
                            >
                              <span className="text-[10px] leading-none">{primaryDist.meta.icon}</span>
                              <span className="font-extrabold">{primaryDist.meta.shortName}</span>
                            </span>
                          ) : (
                            <span className="text-[8px] font-black px-2 py-0.8 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-full tracking-widest uppercase flex items-center gap-1 shrink-0 self-start mt-1">
                              <Wallet className="w-2.5 h-2.5 text-purple-400" />
                              {lang === 'ko' ? '보유' : 'HOLD'}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-col flex-1">
                          <div className="flex-1 flex flex-col justify-center py-6 border-y border-white/5 relative group/value">
                            <div className="text-[9px] font-black text-white/20 uppercase tracking-[0.25em] mb-2 text-center group-hover/value:text-purple-400/50 transition-colors">
                              {lang === 'ko' ? '총 자산 가치' : 'TOTAL MARKET VALUE'}
                            </div>
                            <div className="text-3xl font-black text-white text-center tracking-tighter flex items-baseline justify-center gap-1 group-hover/value:scale-105 transition-transform">
                              {formatCurrency(targetDisplayValue, targetDisplayCurrency)}
                            </div>
                            {hasDiffCurrency && (
                              <div className="text-[11px] font-mono font-medium text-white/40 text-center mt-1">
                                <span className="text-[10px] text-white/30 mr-1">{lang === 'ko' ? '원문' : 'Native'}</span>
                                {formatCurrency(nativeValue, assetMarket)}
                              </div>
                            )}

                            {!isCash && (
                              <div className="flex justify-center mt-2">
                                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black shadow-lg ${pct >= 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
                                  {pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                  {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-6 pt-2">
                            <div className="space-y-1">
                              <div className="text-[9px] font-bold text-white/20 uppercase tracking-wider">{lang === 'ko' ? '보유 수량' : 'QUANTITY'}</div>
                              <div className="text-sm font-mono font-bold text-white/80">
                                {(holding.shares || 0).toLocaleString()} <span className="text-[9px] text-white/20">SH</span>
                              </div>
                            </div>
                            <div className="text-right space-y-1">
                              <div className="text-[9px] font-bold text-white/20 uppercase tracking-wider">{lang === 'ko' ? '비중 (현재 / 목표)' : 'WEIGHT (ACT / TGT)'}</div>
                              <div className="text-sm font-mono font-bold text-purple-400">
                                {((holding.weight || 0) * 100).toFixed(1)}%
                              </div>
                              <div className="text-[10px] text-white/40 font-mono font-bold">
                                {lang === 'ko' ? '목표' : 'Tgt'}: {((holding.targetWeight || 0) * 100).toFixed(1)}%
                              </div>
                            </div>
                          </div>

                          {/* Account Breakdown Bar (방안 3: 복수 계좌 분배 시각화) */}
                          {isMultiAccount ? (
                            <div className="mt-4 pt-3 border-t border-white/5 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-between text-[9px] font-bold text-white/50">
                                <span>{lang === 'ko' ? '증권사별 보유 비중' : 'Broker Distribution'}</span>
                                <span className="font-mono text-purple-300 font-extrabold">{accountDists.length}개사 분산</span>
                              </div>
                              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden flex gap-0.5">
                                {accountDists.map((d, dIdx) => (
                                  <div
                                    key={dIdx}
                                    style={{ width: `${d.pct}%` }}
                                    className={`${d.meta.barColor} h-full transition-all`}
                                    title={`${d.meta.name}: ${d.shares.toLocaleString()}주 (${d.pct.toFixed(1)}%)`}
                                  />
                                ))}
                              </div>
                              <div className="flex flex-wrap gap-1 pt-0.5">
                                {accountDists.map((d, dIdx) => (
                                  <span
                                    key={dIdx}
                                    className={`text-[8px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-1 border shadow-xs ${d.meta.badgeBg}`}
                                    title={`${d.accountName} (${d.shares.toLocaleString()}주)`}
                                  >
                                    <span>{d.meta.icon}</span>
                                    <span>{d.meta.shortName}</span>
                                    <span className="font-mono opacity-85">{d.pct.toFixed(0)}%</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : primaryDist && (
                            <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center justify-between text-[10px] text-white/60 font-semibold" onClick={(e) => e.stopPropagation()}>
                              <span className="flex items-center gap-1.5 truncate pr-2">
                                <span className="text-[11px]">{primaryDist.meta.icon}</span>
                                <span className="text-white/80 font-bold truncate">{primaryDist.accountName}</span>
                              </span>
                              {primaryDist.accountNum && (
                                <span className="font-mono text-white/40 text-[9px] shrink-0 bg-white/5 px-1.5 py-0.5 rounded border border-white/5">
                                  {primaryDist.accountNum.length > 4 ? `***${primaryDist.accountNum.slice(-4)}` : primaryDist.accountNum}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Action Buttons Row */}
                          <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-2 z-20 relative" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isManualEditDisabled) return;
                                setActionHolding(holding);
                                setActionType('adjust');
                              }}
                              disabled={isManualEditDisabled}
                              className={`flex-1 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 shadow-md ${isManualEditDisabled
                                ? 'bg-white/5 border border-white/5 text-white/30 opacity-40 cursor-not-allowed'
                                : 'bg-white/5 border border-white/5 hover:bg-purple-500 hover:border-purple-500 hover:text-white text-white/80 active:scale-95'
                                }`}
                              title={isManualEditDisabled ? editDisabledTitle : (lang === 'ko' ? '포지션 수정' : 'Adjust Position')}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                              <span>{lang === 'ko' ? '수정' : 'ADJ'}</span>
                            </button>
                            {!isCash && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setActionHolding(holding); setActionType('liquidate'); }}
                                className="flex-1 py-2 bg-white/5 border border-white/5 hover:bg-rose-500 hover:border-rose-500 hover:text-white rounded-xl font-black text-[10px] uppercase tracking-widest transition-all text-white/80 flex items-center justify-center gap-1.5 active:scale-95 shadow-md"
                                title={lang === 'ko' ? '청산' : 'Liquidate'}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>{lang === 'ko' ? '청산' : 'LIQ'}</span>
                              </button>
                            )}
                          </div>

                          {/* AI Deep Analysis UI Links */}
                          {!isCash && onSelectStock && (
                            <div
                              className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between z-20 relative"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="text-[9px] font-black text-white/25 uppercase tracking-widest leading-none">
                                {lang === 'ko' ? 'AI 심층 분석' : 'AI DEEP ANALYSIS'}
                              </span>
                              <AnalysisLinks symbol={holding.symbol} />
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                /* Premium Table View */
                <div className="glass-premium rounded-3xl overflow-x-auto custom-scrollbar">
                  <table className="w-full text-left border-collapse min-w-[1000px]">
                    <thead>
                      <tr className="text-[10px] uppercase font-black tracking-[0.2em] text-white/20 border-b border-white/10 bg-white/[0.02]">
                        <th className="py-5 px-8">{lang === 'ko' ? '종목' : 'SYMBOL'}</th>
                        <th className="py-5 px-4">{lang === 'ko' ? '보유 수량' : 'SHARES'}</th>
                        <th className="py-5 px-4">{lang === 'ko' ? '평균 단가' : 'AVG COST'}</th>
                        <th className="py-5 px-4">{lang === 'ko' ? '현재 가격' : 'MARKET PRICE'}</th>
                        <th className="py-5 px-4 text-right">{lang === 'ko' ? '평가 금액' : 'VALUATION'}</th>
                        <th className="py-5 px-4">{lang === 'ko' ? '수익률' : 'RETURN'}</th>
                        <th className="py-5 px-4 text-right">
                          <div className="flex flex-col items-end justify-center">
                            <span>{lang === 'ko' ? '비중' : 'WEIGHT'}</span>
                            <span className="text-[8px] text-white/40 normal-case font-bold tracking-tight">{lang === 'ko' ? '(현재 / 목표)' : '(Actual / Target)'}</span>
                          </div>
                        </th>
                        <th className="py-5 px-8 text-right">{lang === 'ko' ? '관리 / 분석' : 'ACTIONS & ANALYSIS'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {paginatedHoldings.map((holding) => {
                        const { price, pct } = calculateReturn(holding);
                        const isCash = holding.symbol === 'CASH' || holding.symbol === 'USD' || holding.symbol === 'KRW';
                        const signal = signals.find(s => s.symbol.toUpperCase() === holding.symbol.toUpperCase());
                        const assetMarket = holding.market || getAssetMarket(holding.symbol);
                        const targetDisplayCurrency = holding.displayCurrency || (currencyPreference === 'KRW' ? 'KR' : 'US');
                        const targetDisplayPriceCurrency = holding.displayPriceCurrency || targetDisplayCurrency;
                        const targetDisplayValue = holding.displayValue !== undefined ? holding.displayValue : (holding.value || holding.shares * price);
                        const nativeValue = holding.value || holding.shares * price;
                        const targetDisplayAvgCost = holding.displayAvgCost !== undefined ? holding.displayAvgCost : holding.avgCost;
                        const targetDisplayPrice = holding.displayPrice !== undefined ? holding.displayPrice : price;
                        const hasDiffCurrency = assetMarket !== targetDisplayCurrency;
                        let displayName = getHoldingDisplayName(holding, signal, lang);
                        if (isAll && isCash) {
                          if (holding.symbol.toUpperCase() === 'USD') {
                            displayName = lang === 'ko' ? '현금 자산 (달러)' : 'USD Cash';
                          } else if (holding.symbol.toUpperCase() === 'KRW' || holding.symbol.toUpperCase() === 'CASH') {
                            displayName = lang === 'ko' ? '현금 자산 (원화)' : 'KRW Cash';
                          }
                        }

                        const tableAccountDists = getHoldingAccountDistributions(holding, accounts || []);
                        const isTableMultiAccount = tableAccountDists.length > 1;
                        const primaryTableDist = tableAccountDists[0];

                        return (
                          <tr key={holding.symbol} className="hover:bg-white/[0.02] transition-colors group">
                            <td className="py-6 px-8 flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs text-white shadow-sm border ${holding.symbol === 'CASH' || holding.symbol === 'KRW'
                                ? 'bg-blue-500/20 border-blue-500/20 text-blue-400'
                                : holding.symbol === 'USD'
                                  ? 'bg-emerald-500/20 border-emerald-500/20 text-emerald-400'
                                  : 'bg-white/5 border-white/10'
                                }`}>
                                {holding.symbol === 'CASH' || holding.symbol === 'KRW' ? '₩' :
                                  holding.symbol === 'USD' ? '$' :
                                    displayName[0]}
                              </div>
                              <div className="flex flex-col gap-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-sm font-black text-white italic tracking-tighter group-hover:text-purple-400 transition-colors">
                                    {displayName}
                                  </span>
                                  <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-md flex-shrink-0 ${assetMarket === 'KR' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                                    }`}>
                                    {assetMarket}
                                  </span>
                                  {isTableMultiAccount ? (
                                    <span
                                      className="text-[8px] font-black px-1.5 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded flex items-center gap-0.5"
                                      title={tableAccountDists.map(d => `${d.meta.name}: ${d.shares.toLocaleString()}주 (${d.pct.toFixed(0)}%)`).join(', ')}
                                    >
                                      <span>🌐</span>
                                      <span>{tableAccountDists.length}개사</span>
                                    </span>
                                  ) : primaryTableDist ? (
                                    <span
                                      className={`text-[8px] font-black px-1.5 py-0.5 rounded flex items-center gap-0.5 border ${primaryTableDist.meta.badgeBg}`}
                                      title={primaryTableDist.accountName}
                                    >
                                      <span>{primaryTableDist.meta.icon}</span>
                                      <span>{primaryTableDist.meta.shortName}</span>
                                    </span>
                                  ) : null}
                                </div>

                                <span className="text-[9px] text-white/40 font-bold uppercase tracking-widest truncate max-w-[140px]">
                                  {getHoldingSubText(holding, signal, lang)}
                                </span>

                                {/* 종목명 아래 자산유형 옆에 종목별 분석 보기 버튼 배치 */}
                                <div className="flex items-center gap-2 flex-wrap pt-0.5">
                                  <span className="text-[10px] text-white/30 font-bold uppercase tracking-widest leading-none">
                                    {isCash ? (lang === 'ko' ? '유동 자산' : 'CASH') : (lang === 'ko' ? '전략 주식' : 'EQUITY')}
                                  </span>
                                  {!isCash && onSelectStock && (
                                    <div className="flex items-center ml-0.5">
                                      <AnalysisLinks symbol={holding.symbol} />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-6 px-4">
                              <span className="font-mono font-bold text-white/80">{(holding.shares || 0).toLocaleString()}</span>
                            </td>
                            <td className="py-6 px-4">
                              <div className="flex flex-col">
                                <span className="font-mono font-bold text-white/40">{formatCurrency(targetDisplayAvgCost, targetDisplayPriceCurrency)}</span>
                                {hasDiffCurrency && (
                                  <span className="text-[10px] font-mono text-white/20">({formatCurrency(holding.avgCost, assetMarket)})</span>
                                )}
                              </div>
                            </td>
                            <td className="py-6 px-4">
                              <div className="flex flex-col">
                                <span className="font-mono font-bold text-white/80">{formatCurrency(targetDisplayPrice, targetDisplayPriceCurrency)}</span>
                                {hasDiffCurrency && (
                                  <span className="text-[10px] font-mono text-white/20">({formatCurrency(holding.currentPrice || price, assetMarket)})</span>
                                )}
                              </div>
                            </td>
                            <td className="py-6 px-4 text-right">
                              <div className="flex flex-col items-end">
                                <span className="font-mono font-bold text-purple-400">
                                  {formatCurrency(targetDisplayValue, targetDisplayCurrency)}
                                </span>
                                {hasDiffCurrency && (
                                  <span className="text-[10px] font-mono text-white/30">
                                    ({formatCurrency(nativeValue, assetMarket)})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-6 px-4">
                              {!isCash ? (
                                <div className={`flex items-center gap-1 font-black text-sm ${pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                  {Math.abs(pct).toFixed(2)}%
                                </div>
                              ) : (
                                <span className="text-white/20 font-mono text-sm">-</span>
                              )}
                            </td>
                            <td className="py-6 px-4 text-right">
                              <div className="flex flex-col items-end justify-center">
                                <span className="font-mono font-bold text-purple-400 text-lg">{((holding.weight || 0) * 100).toFixed(1)}%</span>
                                <span className="text-[10px] text-white/40 font-mono font-bold">
                                  {lang === 'ko' ? '목표' : 'Target'}: {((holding.targetWeight || 0) * 100).toFixed(1)}%
                                </span>
                              </div>
                            </td>
                            <td className="py-6 px-8">
                              <div className="flex justify-end items-center gap-6">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      if (isManualEditDisabled) return;
                                      setActionHolding(holding);
                                      setActionType('adjust');
                                    }}
                                    disabled={isManualEditDisabled}
                                    className={`px-3 py-1.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${isManualEditDisabled
                                      ? 'bg-white/5 border border-white/5 text-white/30 opacity-40 cursor-not-allowed'
                                      : 'bg-white/5 border border-white/5 hover:bg-purple-500 hover:border-purple-500 text-white active:scale-95'
                                      }`}
                                    title={isManualEditDisabled ? editDisabledTitle : (lang === 'ko' ? '포지션 수정' : 'Adjust Position')}
                                  >
                                    {lang === 'ko' ? '수정' : 'ADJ'}
                                  </button>
                                  {!isCash && (
                                    <button
                                      onClick={() => { setActionHolding(holding); setActionType('liquidate'); }}
                                      className="px-3 py-1.5 bg-white/5 border border-white/5 hover:bg-rose-500 hover:border-rose-500 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all text-white active:scale-95"
                                    >
                                      {lang === 'ko' ? '청산' : 'LIQ'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {sortedHoldings.length > 0 && (
                <PaginationControls
                  currentPage={holdingsPage}
                  totalPages={totalHoldingsPages}
                  totalItems={sortedHoldings.length}
                  pageSize={holdingsPageSize}
                  onPageChange={setHoldingsPage}
                  onPageSizeChange={(size) => {
                    setHoldingsPageSize(size);
                    setHoldingsPage(1);
                  }}
                  lang={lang}
                />
              )}

              {holdings.length === 0 && (
                <div className="py-32 text-center rounded-3xl border border-dashed border-white/10 bg-white/[0.01]">
                  <Wallet className="w-16 h-16 text-white/5 mx-auto mb-6" />
                  <p className="text-white/40 text-lg font-bold italic">
                    {market === 'KR'
                      ? (lang === 'ko' ? '한국 증시 보유 종목이 없습니다.' : 'No Korean market holdings yet.')
                      : market === 'US'
                        ? (lang === 'ko' ? '미국 증시 보유 종목이 없습니다.' : 'No US market holdings yet.')
                        : (lang === 'ko' ? '보유 자산이 없습니다.' : 'No assets in your portfolio yet.')
                    }
                  </p>
                  <p className="text-white/20 text-xs mt-2 font-bold">
                    {market === 'KR'
                      ? (lang === 'ko' ? 'KOSPI / KOSDAQ 종목을 추가하여 한국 포트폴리오를 시작하세요.' : 'Add KOSPI or KOSDAQ stocks to start your Korean portfolio.')
                      : (lang === 'ko' ? 'NYSE / NASDAQ 종목을 추가하여 미국 포트폴리오를 시작하세요.' : 'Add NYSE or NASDAQ stocks to build your US portfolio.')
                    }
                  </p>
                  {isAddAssetEnabled && canAddAsset && (
                    <button
                      onClick={() => onAdd('stock')}
                      className="mt-8 px-8 py-3 glass-premium text-white hover:bg-white hover:text-black transition-all rounded-full font-black uppercase text-xs tracking-widest"
                    >
                      {market === 'KR' ? (lang === 'ko' ? '한국 종목 추가' : 'Add KR Stock') : (lang === 'ko' ? '미국 종목 추가' : 'Add US Stock')}
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="watchlist"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6 animate-in fade-in duration-300"
            >
              {/* Simulator Global Stats Badge */}
              <div className="flex justify-end mb-4">
                <div className="glass-premium px-6 py-3 rounded-2xl border-white/5 flex items-center gap-4 text-xs bg-white/[0.01]">
                  <div className="flex flex-col">
                    <span className="text-white/30 font-bold uppercase tracking-widest text-[9px]">{lang === 'ko' ? '현재 포트폴리오 베타' : 'CURRENT PORTFOLIO BETA'}</span>
                    <span className="text-sm font-black font-mono mt-0.5 text-white/80">{currentPortfolioBeta.toFixed(2)}</span>
                  </div>
                  <div className="w-px h-8 bg-white/10" />
                  <div className="flex flex-col">
                    <span className="text-white/30 font-bold uppercase tracking-widest text-[9px]">{lang === 'ko' ? '보유 가용 현금' : 'LIQUID CASH'}</span>
                    <span className="text-sm font-black font-mono mt-0.5 text-purple-400">
                      {formatCurrency(liquidCash, market === 'ALL' ? 'US' : market as 'US' | 'KR')}
                    </span>
                  </div>
                </div>
              </div>

              {viewMode === 'grid' ? (
                /* Premium Watchlist Grid View (Mobile Friendly) */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {paginatedWatchlist.map((item, idx) => {
                    const signal = signals.find(s => s.symbol.toUpperCase() === item.symbol.toUpperCase());
                    const price = (signal?.price && signal.price > 0) ? signal.price : 0;
                    const pctChange = signal?.pctChange || 0;

                    const simShare = simulatedShares[item.symbol] || 0;
                    const simPrice = simulatedPrices[item.symbol] || price;
                    const simBuyValue = simShare * simPrice;
                    const isExpanded = !!expandedSimulation[item.symbol];
                    const isInsufficientCash = simBuyValue > liquidCash;

                    const newTotalValue = totalValue + simBuyValue;
                    const simWeight = newTotalValue > 0 ? (simBuyValue / newTotalValue) * 100 : 0;

                    const projectedBeta = newTotalValue > 0 ? (
                      holdings.reduce((sum, h) => {
                        const isHgetCash = h.symbol === 'CASH' || h.symbol === 'USD' || h.symbol === 'KRW';
                        const hSignal = signals.find(s => s.symbol.toUpperCase() === h.symbol.toUpperCase());
                        const hPrice = isHgetCash ? 1 : ((hSignal?.price && hSignal.price > 0) ? hSignal.price : h.avgCost);
                        const hVal = h.shares * hPrice;
                        const hWeight = hVal / newTotalValue;
                        const hBeta = isHgetCash ? 0 : getStockBeta(h.symbol);
                        return sum + (hWeight * hBeta);
                      }, 0) + (simBuyValue / newTotalValue) * getStockBeta(item.symbol)
                    ) : 0;

                    const itemSignal = signals.find(s => s.symbol.toUpperCase() === item.symbol.toUpperCase());
                    const itemDisplayName = getHoldingDisplayName({ symbol: item.symbol, name: item.name }, itemSignal, lang);
                    const itemSubText = getHoldingSubText({ symbol: item.symbol, name: item.name }, itemSignal, lang);

                    return (
                          <motion.div
                            key={item.symbol}
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            onClick={() => onSelectTechnicalStock && onSelectTechnicalStock(item.symbol)}
                            className={`glass-premium p-6 rounded-[2rem] relative overflow-hidden group premium-gradient-bg transition-all mb-2 border border-amber-500/10 ${!isTechnicalAnalysisEnabled ? 'cursor-default' : 'cursor-pointer hover:border-amber-500/30'}`}
                          >
                            <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/[0.02] transition-colors duration-300 pointer-events-none" />

                            <div className="flex items-start justify-between gap-3 mb-4 min-w-0">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-base font-black text-amber-500 bg-amber-500/10 border border-amber-500/20 shadow-sm flex-shrink-0">
                                  {itemDisplayName[0]}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <h3
                                      className={`font-black text-white tracking-tight group-hover:text-amber-400 transition-colors leading-snug break-words ${
                                        itemDisplayName.length > 24 ? 'text-sm sm:text-base' : itemDisplayName.length > 15 ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'
                                      }`}
                                      title={itemDisplayName}
                                    >
                                      {itemDisplayName}
                                    </h3>
                                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-md flex-shrink-0 ${item.market === 'KR' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                                      }`}>
                                      {item.market}
                                    </span>
                                  </div>
                                  <p className="text-xs font-bold text-white/40 mt-0.5 uppercase tracking-wider truncate">
                                    {itemSubText}
                                  </p>
                                </div>
                              </div>

                              <span className="text-[8px] font-black px-2 py-0.8 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-full tracking-widest uppercase flex items-center gap-1 shrink-0 self-start mt-1">
                                <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
                                {lang === 'ko' ? '관심' : 'WATCH'}
                              </span>
                            </div>

                        <div className="py-4 border-y border-white/5 flex flex-col justify-center items-center">
                          <div className="text-[9px] font-black text-white/20 uppercase tracking-[0.25em] mb-1">
                            {lang === 'ko' ? '실시간 가격' : 'LIVE MARKET PRICE'}
                          </div>
                          <div className="text-2xl font-black text-white tracking-tighter">
                            {price > 0 ? formatCurrency(price, item.market) : (
                              <span className="text-white/20 italic animate-pulse">{lang === 'ko' ? '동기화 중...' : 'Syncing...'}</span>
                            )}
                          </div>
                          {price > 0 && (
                            <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 mt-1.5 rounded-full text-[9px] font-black shadow-lg ${pctChange >= 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
                              {pctChange >= 0 ? '+' : ''}{pctChange.toFixed(2)}%
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 my-4">
                          <div className="space-y-0.5">
                            <div className="text-[9px] font-bold text-white/20 uppercase tracking-wider">{lang === 'ko' ? '공매도 (수급)' : 'SHORT INT.'}</div>
                            <div className="text-xs font-mono font-bold text-white/80">
                              {signal && signal.shortRatio > 0 ? `${signal.shortRatio.toFixed(1)}%` : '-'}
                            </div>
                          </div>
                          <div className="text-right space-y-0.5">
                            <div className="text-[9px] font-bold text-white/20 uppercase tracking-wider">{lang === 'ko' ? '리스크 점수' : 'RISK SCORE'}</div>
                            <div className="text-xs font-mono font-bold">
                              {signal ? (
                                <span className={signal.totalScore >= 50 ? 'text-red-400' : 'text-emerald-400'}>{signal.totalScore} pts</span>
                              ) : '-'}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-2 z-20 relative" onClick={(e) => e.stopPropagation()}>
                          {holdingsLimit !== undefined && holdingsLimit !== -1 && holdingsCount !== undefined && holdingsCount >= holdingsLimit ? (
                            <div className="flex-1 flex items-center gap-1 px-2 py-2 bg-amber-500/10 border border-amber-500/20 rounded-xl" title={lang === 'ko' ? '보유 종목 한도 초과' : 'Holdings limit reached'}>
                              <Info className="w-3 h-3 text-amber-400 shrink-0" />
                              <span className="text-[8px] font-bold text-amber-400 leading-tight">
                                {lang === 'ko' ? `한도(${holdingsLimit}개) 초과` : `Limit(${holdingsLimit}) reached`}
                              </span>
                            </div>
                          ) : (
                            <button
                              onClick={() => onPromoteToHolding?.(item.symbol, simShare > 0 ? simShare : 10, simPrice > 0 ? simPrice : price)}
                              className="flex-1 py-2 bg-emerald-500 text-black hover:bg-emerald-400 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all active:scale-95 shadow-md flex items-center justify-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              <span>{lang === 'ko' ? '편입' : 'PROM'}</span>
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setExpandedSimulation(prev => ({ ...prev, [item.symbol]: !prev[item.symbol] }));
                              if (!simulatedPrices[item.symbol] && price > 0) {
                                setSimulatedPrices(prev => ({ ...prev, [item.symbol]: price }));
                              }
                            }}
                            className={`flex-1 py-2 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all flex items-center justify-center gap-1 ${isExpanded
                              ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/20'
                              : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10 border border-white/5'
                              }`}
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            <span>{lang === 'ko' ? '시뮬' : 'SIM'}</span>
                          </button>
                          <button
                            onClick={() => onRemoveWatchlist?.(item.symbol, item.market)}
                            className="p-2 bg-white/5 hover:bg-rose-500/20 hover:text-rose-400 rounded-xl transition-all active:scale-95 text-white/40 border border-white/5"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* AI Deep Analysis UI Links */}
                        {onSelectStock && (
                          <div
                            className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between z-20 relative"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-[9px] font-black text-white/25 uppercase tracking-widest leading-none">
                              {lang === 'ko' ? 'AI 심층 분석' : 'AI DEEP ANALYSIS'}
                            </span>
                            <AnalysisLinks symbol={item.symbol} full={true} />
                          </div>
                        )}

                        {isExpanded && (
                          <div
                            className="mt-4 pt-4 border-t border-purple-500/20 bg-purple-500/[0.02] p-4 rounded-2xl animate-in fade-in slide-in-from-top-2 duration-300 z-20 relative"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="space-y-3">
                              <div className="text-[10px] text-purple-400 font-bold uppercase tracking-wider flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                {lang === 'ko' ? '비중 예측' : 'PROJECTION'}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-[10px]">
                                <div>
                                  <label className="text-white/40 block mb-1">{lang === 'ko' ? '가상 수량' : 'SHARES'}</label>
                                  <input
                                    type="number"
                                    value={simShare || ''}
                                    onChange={(e) => setSimulatedShares(prev => ({ ...prev, [item.symbol]: Number(e.target.value) }))}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-1.5 text-center text-white focus:outline-none focus:border-purple-500"
                                  />
                                </div>
                                <div>
                                  <label className="text-white/40 block mb-1">{lang === 'ko' ? '가상 단가' : 'PRICE'}</label>
                                  <input
                                    type="number"
                                    value={simulatedPrices[item.symbol] || ''}
                                    onChange={(e) => setSimulatedPrices(prev => ({ ...prev, [item.symbol]: Number(e.target.value) }))}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-1.5 text-center text-white focus:outline-none focus:border-purple-500"
                                  />
                                </div>
                              </div>
                              {simShare > 0 && (
                                <div className="bg-white/5 p-2 rounded-xl text-[10px] space-y-1.5">
                                  <div className="flex justify-between">
                                    <span className="text-white/40">{lang === 'ko' ? '필요 자금' : 'Required Cash'}:</span>
                                    <span className={`font-mono ${isInsufficientCash ? 'text-red-400 font-bold' : 'text-white/80'}`}>
                                      {formatCurrency(simBuyValue, item.market)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-white/40">{lang === 'ko' ? '예상 비중' : 'Proj. Weight'}:</span>
                                    <span className="font-mono text-purple-400 font-bold">{simWeight.toFixed(1)}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-white/40">{lang === 'ko' ? '포트폴리오 베타' : 'Proj. Beta'}:</span>
                                    <span className="font-mono text-white/80">{projectedBeta.toFixed(2)}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* View Analysis Hint */}
                        {isTechnicalAnalysisEnabled && (
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-10">
                            <div className="px-3 py-1 bg-amber-500 text-black text-[9px] font-black uppercase tracking-widest rounded-full shadow-lg shadow-amber-500/20 translate-y-2 group-hover:translate-y-0 transition-transform">
                              {lang === 'ko' ? '기술적 분석 보기' : 'View Analysis'}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                /* Watchlist Table View */
                <div className="glass-premium rounded-3xl overflow-hidden border-white/5 flex flex-col w-full">
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left border-collapse min-w-[1000px]">
                      <thead>
                        <tr className="text-[10px] uppercase font-black tracking-[0.2em] text-white/20 border-b border-white/10 bg-white/[0.02]">
                          <th className="py-5 px-8">{lang === 'ko' ? '종목 / 코드' : 'SYMBOL / CODE'}</th>
                          <th className="py-5 px-4">{lang === 'ko' ? '실시간 가격' : 'LIVE PRICE'}</th>
                          <th className="py-5 px-4">{lang === 'ko' ? '공매도 비율 (수급)' : 'SHORT INTEREST'}</th>
                          <th className="py-5 px-4">{lang === 'ko' ? '옵션 시그널' : 'OPTIONS DATA'}</th>
                          <th className="py-5 px-4">{lang === 'ko' ? '리스크 분석' : 'RISK SCORE'}</th>
                          <th className="py-5 px-4 text-center">{lang === 'ko' ? '편입 시뮬레이션' : 'SIMULATION'}</th>
                          <th className="py-5 px-8 text-right">{lang === 'ko' ? '작업' : 'ACTIONS'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {paginatedWatchlist.map((item) => {
                          const signal = signals.find(s => s.symbol.toUpperCase() === item.symbol.toUpperCase());
                          const price = (signal?.price && signal.price > 0) ? signal.price : 0;
                          const pctChange = signal?.pctChange || 0;

                          // Sizing simulator variables
                          const simShare = simulatedShares[item.symbol] || 0;
                          const simPrice = simulatedPrices[item.symbol] || price;
                          const simBuyValue = simShare * simPrice;
                          const isExpanded = !!expandedSimulation[item.symbol];

                          // Calculate weight & beta impact
                          const newTotalValue = totalValue + simBuyValue;
                          const simWeight = newTotalValue > 0 ? (simBuyValue / newTotalValue) * 100 : 0;

                          // Projected Portfolio Beta calculation
                          const projectedBeta = newTotalValue > 0 ? (
                            holdings.reduce((sum, h) => {
                              const isHgetCash = h.symbol === 'CASH' || h.symbol === 'USD' || h.symbol === 'KRW';
                              const hSignal = signals.find(s => s.symbol.toUpperCase() === h.symbol.toUpperCase());
                              const hPrice = isHgetCash ? 1 : ((hSignal?.price && hSignal.price > 0) ? hSignal.price : h.avgCost);
                              const hVal = h.shares * hPrice;
                              const hWeight = hVal / newTotalValue;
                              const hBeta = isHgetCash ? 0 : getStockBeta(h.symbol);
                              return sum + (hWeight * hBeta);
                            }, 0) + (simBuyValue / newTotalValue) * getStockBeta(item.symbol)
                          ) : 0;

                          const isInsufficientCash = simBuyValue > liquidCash;

                          return (
                            <React.Fragment key={item.symbol}>
                              <tr key={item.symbol} className="hover:bg-white/[0.02] transition-colors group">
                                <td className="py-6 px-8 flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs text-white shadow-sm border bg-white/5 border-white/10 group-hover:border-amber-500/30 transition-all flex-shrink-0`}>
                                    {item.symbol[0]}
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <div className="flex items-center gap-3">
                                      <div className="flex flex-col">
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-sm font-black text-white group-hover:text-amber-400 transition-colors uppercase italic tracking-tighter">
                                            {item.symbol}
                                          </span>
                                          <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-md flex-shrink-0 ${item.market === 'KR' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                                            }`}>
                                            {item.market}
                                          </span>
                                        </div>
                                        {item.name || COMMON_NAMES[item.symbol.toUpperCase()] ? (
                                          <span className="text-[9px] text-white/30 font-bold uppercase tracking-widest truncate max-w-[120px]">
                                            {item.name || COMMON_NAMES[item.symbol.toUpperCase()]}
                                          </span>
                                        ) : null}
                                      </div>
                                      {onSelectStock && (
                                        <AnalysisLinks symbol={item.symbol} />
                                      )}
                                    </div>
                                    <div className="text-[10px] text-white/30 font-bold uppercase tracking-widest leading-none">
                                      {lang === 'ko' ? '관심 주식' : 'WATCHLIST'}
                                    </div>
                                  </div>
                                </td>
                                <td className="py-6 px-4">
                                  <div className="font-mono font-bold text-white/80">
                                    {price > 0 ? formatCurrency(price, item.market) : (
                                      <span className="text-white/20 italic animate-pulse">{lang === 'ko' ? '동기화 중...' : 'Syncing...'}</span>
                                    )}
                                  </div>
                                  {price > 0 && (
                                    <div className={`text-[10px] font-bold ${pctChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                      {pctChange >= 0 ? '+' : ''}{pctChange.toFixed(2)}%
                                    </div>
                                  )}
                                </td>

                                <td className="py-6 px-4">
                                  {signal && signal.shortRatio > 0 ? (
                                    <div>
                                      <span className="font-mono font-bold text-white/80">{signal.shortRatio.toFixed(2)}%</span>
                                      {signal.daysToCover > 0 && (
                                        <div className="text-[9px] text-white/30">{signal.daysToCover} DTC</div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-white/20 font-mono text-sm">-</span>
                                  )}
                                </td>

                                <td className="py-6 px-4">
                                  {signal && (signal.ivRank > 0 || signal.putCallRatio > 0) ? (
                                    <div className="text-[10px] space-y-0.5 font-mono">
                                      <div className="flex justify-between gap-2">
                                        <span className="text-white/30">IV Rank:</span>
                                        <span className="text-white/70">{signal.ivRank}%</span>
                                      </div>
                                      <div className="flex justify-between gap-2">
                                        <span className="text-white/30">P/C Ratio:</span>
                                        <span className="text-white/70">{signal.putCallRatio.toFixed(2)}</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-white/20 font-mono text-sm">-</span>
                                  )}
                                </td>

                                <td className="py-6 px-4">
                                  {signal ? (
                                    <div className={`inline-flex px-2 py-0.5 rounded-md text-[9px] font-bold border ${getAlertColor(signal.alertLevel)}`}>
                                      {signal.totalScore} | {signal.alertLevel}
                                    </div>
                                  ) : (
                                    <span className="text-white/20 font-mono text-sm">-</span>
                                  )}
                                </td>

                                <td className="py-6 px-4 text-center">
                                  <button
                                    onClick={() => {
                                      setExpandedSimulation(prev => ({ ...prev, [item.symbol]: !prev[item.symbol] }));
                                      if (!simulatedPrices[item.symbol] && price > 0) {
                                        setSimulatedPrices(prev => ({ ...prev, [item.symbol]: price }));
                                      }
                                    }}
                                    className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5 mx-auto ${isExpanded
                                      ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/20'
                                      : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                                      }`}
                                  >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    <span>{lang === 'ko' ? '시뮬레이터' : 'Simulate'}</span>
                                  </button>
                                </td>

                                <td className="py-6 px-8 text-right">
                                  <div className="flex justify-end items-center gap-2">
                                    {holdingsLimit !== undefined && holdingsLimit !== -1 && holdingsCount !== undefined && holdingsCount >= holdingsLimit ? (
                                      <div className="flex items-center gap-1 px-2 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-xl" title={lang === 'ko' ? '보유 종목 한도 초과' : 'Holdings limit reached'}>
                                        <Info className="w-3 h-3 text-amber-400 shrink-0" />
                                        <span className="text-[8px] font-bold text-amber-400 whitespace-nowrap">
                                          {lang === 'ko' ? `한도(${holdingsLimit}개) 초과` : `Limit(${holdingsLimit})`}
                                        </span>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => onPromoteToHolding?.(item.symbol, simShare > 0 ? simShare : 10, simPrice > 0 ? simPrice : price)}
                                        className="px-3 py-1.5 bg-emerald-500 text-black hover:bg-emerald-400 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all active:scale-95 shadow-md shadow-emerald-500/10 flex items-center gap-1"
                                        title={lang === 'ko' ? '포트폴리오 편입' : 'Promote to Holding'}
                                      >
                                        <Plus className="w-3 h-3" />
                                        <span>{lang === 'ko' ? '편입' : 'PROM'}</span>
                                      </button>
                                    )}
                                    <button
                                      onClick={() => onRemoveWatchlist?.(item.symbol, item.market)}
                                      className="p-1.5 bg-white/5 hover:bg-rose-500/20 hover:text-rose-400 rounded-xl transition-all active:scale-95 text-white/40 border border-white/5 hover:border-rose-500/20"
                                      title={lang === 'ko' ? '관심종목 해제' : 'Remove from watchlist'}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>

                              {/* Simulation Expansion Board */}
                              {isExpanded && (
                                <tr className="bg-purple-500/[0.02] border-x-2 border-purple-500/20 animate-in fade-in slide-in-from-top-2 duration-300">
                                  <td colSpan={7} className="p-6 border-b border-white/10">
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                                      <div className="lg:col-span-4 space-y-4">
                                        <div className="flex items-center gap-2 text-purple-400 font-bold text-xs uppercase tracking-widest">
                                          <Sparkles className="w-4 h-4" />
                                          {lang === 'ko' ? '편입 시뮬레이터 (What-If)' : 'Position Sizing Simulator'}
                                        </div>
                                        <p className="text-[11px] text-white/50 leading-relaxed font-medium">
                                          {lang === 'ko'
                                            ? "가상으로 매수할 수량과 가격을 입력하면, 전체 포트폴리오 가치 변화량, 예상 가상 비중 및 포트폴리오 베타의 영향도를 실시간으로 모니터링합니다."
                                            : "Enter hypothetical shares and buy price to view the projected portfolio weight, required cash, and portfolio Beta impact in real-time."}
                                        </p>
                                      </div>

                                      {/* Inputs */}
                                      <div className="lg:col-span-4 grid grid-cols-2 gap-4">
                                        <div>
                                          <label className="text-[9px] text-white/40 font-bold uppercase tracking-wider block mb-1.5">{lang === 'ko' ? '시뮬레이션 수량' : 'SIM SHARES'}</label>
                                          <input
                                            type="number"
                                            value={simShare || ''}
                                            onChange={(e) => setSimulatedShares(prev => ({ ...prev, [item.symbol]: Math.max(0, Number(e.target.value)) }))}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm font-mono font-bold text-white outline-none focus:border-purple-500 transition-all bg-black/40"
                                            placeholder="e.g. 100"
                                          />
                                        </div>
                                        <div>
                                          <label className="text-[9px] text-white/40 font-bold uppercase tracking-wider block mb-1.5">{lang === 'ko' ? '시뮬레이션 단가' : 'SIM PRICE'}</label>
                                          <input
                                            type="number"
                                            value={simPrice || ''}
                                            onChange={(e) => setSimulatedPrices(prev => ({ ...prev, [item.symbol]: Math.max(0, Number(e.target.value)) }))}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm font-mono font-bold text-white outline-none focus:border-purple-500 transition-all bg-black/40"
                                            placeholder="Price"
                                          />
                                        </div>
                                      </div>

                                      {/* Real-time Outputs */}
                                      <div className="lg:col-span-4 grid grid-cols-3 gap-3">
                                        <div className="p-3 bg-black/25 border border-white/5 rounded-2xl text-center">
                                          <div className="text-[8px] text-white/30 font-bold uppercase tracking-wider mb-1">{lang === 'ko' ? '예상 가상 비중' : 'EST. WEIGHT'}</div>
                                          <div className="text-base font-black font-mono text-purple-400">{simWeight.toFixed(1)}%</div>
                                        </div>

                                        <div className="p-3 bg-black/25 border border-white/5 rounded-2xl text-center">
                                          <div className="text-[8px] text-white/30 font-bold uppercase tracking-wider mb-1">{lang === 'ko' ? '예상 포트베타' : 'PORT BETA'}</div>
                                          <div className="text-base font-black font-mono text-white/80">
                                            {projectedBeta > 0 ? projectedBeta.toFixed(2) : '-'}
                                          </div>
                                          {projectedBeta > 0 && (
                                            <div className={`text-[8px] font-bold ${projectedBeta > currentPortfolioBeta ? 'text-amber-400' : 'text-emerald-400'}`}>
                                              {projectedBeta > currentPortfolioBeta ? '▲' : '▼'} {(projectedBeta - currentPortfolioBeta).toFixed(2)}
                                            </div>
                                          )}
                                        </div>

                                        <div className={`p-3 border rounded-2xl text-center ${isInsufficientCash ? 'bg-rose-500/5 border-rose-500/20 text-rose-400' : 'bg-black/25 border border-white/5 text-emerald-400'
                                          }`}>
                                          <div className="text-[8px] text-white/30 font-bold uppercase tracking-wider mb-1">{lang === 'ko' ? '소요 현금' : 'REQ. CASH'}</div>
                                          <div className="text-xs font-mono font-bold truncate">
                                            {formatCurrency(simBuyValue, item.market)}
                                          </div>
                                          {isInsufficientCash && simBuyValue > 0 && (
                                            <div className="text-[7px] text-rose-500 font-bold uppercase flex items-center justify-center gap-0.5 mt-0.5">
                                              <Info className="w-2.5 h-2.5 animate-pulse" />
                                              <span>{lang === 'ko' ? '현금 부족' : 'CAP REACH'}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {filteredWatchlist.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-16 text-center text-white/30 font-bold">
                              <Star className="w-12 h-12 text-white/5 mx-auto mb-4" />
                              <div>
                                {market === 'KR'
                                  ? (lang === 'ko' ? '등록된 한국 관심 종목이 없습니다.' : 'No Korean watchlist items.')
                                  : market === 'US'
                                    ? (lang === 'ko' ? '등록된 미국 관심 종목이 없습니다.' : 'No US watchlist items.')
                                    : (lang === 'ko' ? '등록된 관심 종목이 없습니다.' : 'Your watchlist is empty.')
                                }
                              </div>
                              <p className="text-[10px] text-white/20 font-bold mt-1">
                                {lang === 'ko' ? '상단의 빠른 추가 또는 아래 시장 시그널 표에서 ⭐ 아이콘을 눌러 추가하세요.' : 'Add tickers using the quick form or click ⭐ in the signals table below.'}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {filteredWatchlist.length > 0 && (
                <PaginationControls
                  currentPage={watchlistPage}
                  totalPages={totalWatchlistPages}
                  totalItems={filteredWatchlist.length}
                  pageSize={watchlistPageSize}
                  onPageChange={setWatchlistPage}
                  onPageSizeChange={(size) => {
                    setWatchlistPageSize(size);
                    setWatchlistPage(1);
                  }}
                  lang={lang}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <PositionActionModal
        isOpen={!!actionHolding}
        onClose={() => { setActionHolding(null); setActionType(null); }}
        holding={actionHolding}
        action={actionType}
        onConfirm={handleConfirmWithProgress}
        language={lang}
        signals={signals}
        processingState={processingState}
      />
    </div>
  );
}

// ==========================================
// Pagination Controls Component
// ==========================================
interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  lang: string;
}

function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  lang,
}: PaginationControlsProps) {
  if (totalItems === 0) return null;

  const startIdx = (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-2 mt-4 border-t border-white/5">
      <div className="text-xs text-white/40 font-medium">
        {lang === 'ko' ? (
          <span>
            전체 <strong className="text-white font-mono">{totalItems}</strong>개 중{' '}
            <strong className="text-white font-mono">{startIdx}-{endIdx}</strong>개 표시 (
            <span className="text-purple-400 font-mono font-bold">{currentPage}</span> / {totalPages} 페이지)
          </span>
        ) : (
          <span>
            Showing <strong className="text-white font-mono">{startIdx}-{endIdx}</strong> of{' '}
            <strong className="text-white font-mono">{totalItems}</strong> (Page{' '}
            <span className="text-purple-400 font-mono font-bold">{currentPage}</span> of {totalPages})
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-white/30 uppercase tracking-wider font-bold">
              {lang === 'ko' ? '페이지당' : 'Per Page'}
            </span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white/80 font-mono focus:outline-none focus:border-purple-500 cursor-pointer"
            >
              <option value={8}>8</option>
              <option value={16}>16</option>
              <option value={24}>24</option>
            </select>
          </div>
        )}

        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            className="p-1.5 rounded-lg border border-white/10 bg-white/5 text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-all active:scale-95"
            title={lang === 'ko' ? '이전 페이지' : 'Previous page'}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
            const isNearCurrent = Math.abs(p - currentPage) <= 1;
            const isFirstOrLast = p === 1 || p === totalPages;
            if (!isNearCurrent && !isFirstOrLast) {
              if (p === 2 || p === totalPages - 1) {
                return (
                  <span key={`ellipsis-${p}`} className="px-1 text-xs text-white/20 font-mono">
                    ...
                  </span>
                );
              }
              return null;
            }
            return (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`w-8 h-8 rounded-lg text-xs font-mono font-bold transition-all ${
                  p === currentPage
                    ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/20 font-black scale-105'
                    : 'border border-white/5 bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                {p}
              </button>
            );
          })}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="p-1.5 rounded-lg border border-white/10 bg-white/5 text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-all active:scale-95"
            title={lang === 'ko' ? '다음 페이지' : 'Next page'}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Position Adjust / Liquidate Modal Component
// ==========================================
interface PositionActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  holding: Holding | null;
  action: 'adjust' | 'liquidate' | null;
  onConfirm: (shares: number, priceOrAvgCost: number, fee: number) => Promise<void> | void;
  language: string;
  signals: MarketSignal[];
  processingState: {
    isProcessing: boolean;
    stage: string;
    message: string;
    result?: 'success' | 'error';
    resultMessage?: string;
  };
}

function PositionActionModal({
  isOpen,
  onClose,
  holding,
  action,
  onConfirm,
  language,
  signals,
  processingState
}: PositionActionModalProps) {
  if (!isOpen || !holding || !action) return null;

  const isCash = holding.symbol === 'CASH' || holding.symbol === 'USD' || holding.symbol === 'KRW';
  const signal = signals.find(s => s.symbol.toUpperCase() === holding.symbol.toUpperCase());
  const currentPrice = isCash ? 1 : ((signal?.price && signal.price > 0) ? signal.price : holding.avgCost);
  // 🛡️ 청산 단가 기본값: currentPrice가 유효하면 사용, 아니면 avgCost fallback
  const liquidationDefaultPrice = (currentPrice > 0) ? currentPrice : (holding.avgCost || 0);
  const assetMarket = holding.market || (holding.symbol.endsWith('.KS') || holding.symbol.endsWith('.KQ') ? 'KR' : 'US');

// eslint-disable-next-line
  const [shares, setShares] = useState<number>(holding.shares);
// eslint-disable-next-line
  const [priceOrAvgCost, setPriceOrAvgCost] = useState<number>(
    action === 'adjust' ? holding.avgCost : liquidationDefaultPrice
  );
// eslint-disable-next-line
  const [fee, setFee] = useState<number>(0);

  const t = {
    adjustTitle: language === 'ko' ? '포지션 수정' : 'Adjust Position',
    liquidateTitle: language === 'ko' ? '보유 자산 청산' : 'Liquidate Asset',
    shares: language === 'ko' ? '수량 (주)' : 'Shares',
    sharesToLiq: language === 'ko' ? '청산 수량 (주)' : 'Shares to Liquidate',
    avgCost: language === 'ko' ? '평균 단가' : 'Avg Cost',
    liqPrice: language === 'ko' ? '청산 단가' : 'Liquidation Price',
    fee: language === 'ko' ? '거래 수수료' : 'Transaction Fee',
    cancel: language === 'ko' ? '취소' : 'Cancel',
    confirmAdjust: language === 'ko' ? '포지션 수정 완료' : 'Update Position',
    confirmLiq: language === 'ko' ? '청산 실행' : 'Execute Liquidation',
    profitPreview: language === 'ko' ? '예상 실현 손익' : 'Expected Profit/Loss',
    cashInflow: language === 'ko' ? '포트폴리오 현금 편입액 (매도대금 전액)' : 'Net Cash Impact (Total Proceeds)',
    liqWarning: language === 'ko' ? '청산 후 해당 자산은 포트폴리오에서 제외됩니다.' : 'Asset will be hidden from portfolio after full liquidation.'
  };

  // Profit/Loss calculations for Liquidation
  const costBasis = (holding.avgCost || 0) * shares;
  const netProfitLoss = (priceOrAvgCost - (holding.avgCost || 0)) * shares - fee;
  const profitPct = holding.avgCost ? (netProfitLoss / costBasis) * 100 : 0;

  const handleConfirm = async () => {
    // processingState.isProcessing가 true면 중복 실행 방지
    if (processingState.isProcessing) return;
    try {
      await onConfirm(shares, priceOrAvgCost, fee);
      // onConfirm (handleConfirmWithProgress)이 자체적으로 모달 닫기를 관리하므로 여기서 onClose() 호출 안 함
    } catch (e) {
      console.error(`[PositionActionModal] ${action} failed:`, e);
      // 에러 발생 시 모달을 닫지 않고 유지 (사용자가 재시도 가능)
    }
  };

  // ── 진행 상태 오버레이 (TransactionProgressModal) ──
  const renderProgressOverlay = () => {
    if (!processingState.isProcessing && !processingState.result) return null;

    const stageLabels: Record<string, { label: string; icon: string }> = {
      validating: { label: language === 'ko' ? '거래 정보 검증' : 'Validating', icon: '🔍' },
      executing: { label: language === 'ko' ? '거래 실행' : 'Executing', icon: '⚡' },
      saving: { label: language === 'ko' ? '거래 내역 저장' : 'Saving', icon: '💾' },
      done: { label: language === 'ko' ? '완료' : 'Done', icon: '✅' },
      error: { label: language === 'ko' ? '오류' : 'Error', icon: '❌' },
    };

    const stages = ['validating', 'executing', 'saving'];
    const currentStageIdx = stages.indexOf(processingState.stage);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/90 backdrop-blur-md rounded-3xl z-50 flex flex-col items-center justify-center p-8"
      >
        {processingState.isProcessing ? (
          /* 진행 중 */
          <div className="flex flex-col items-center gap-6 w-full max-w-sm">
            {/* Spinner */}
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-white/10 rounded-full" />
              <div className="absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin" />
            </div>

            {/* Stage Progress Steps */}
            <div className="w-full space-y-3">
              {stages.map((stage, idx) => {
                const isActive = idx === currentStageIdx;
                const isPast = idx < currentStageIdx;
                const stageInfo = stageLabels[stage];

                return (
                  <div key={stage} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black transition-all duration-300 ${isPast
                      ? 'bg-emerald-500 text-white'
                      : isActive
                        ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30 animate-pulse'
                        : 'bg-white/10 text-white/30'
                      }`}>
                      {isPast ? '✓' : isActive ? '●' : '○'}
                    </div>
                    <span className={`text-xs font-bold uppercase tracking-wider transition-all duration-300 ${isPast
                      ? 'text-emerald-400'
                      : isActive
                        ? 'text-purple-400'
                        : 'text-white/30'
                      }`}>
                      {stageInfo.label}
                    </span>
                    {isActive && (
                      <span className="text-[10px] text-white/50 font-medium ml-auto animate-pulse">
                        {processingState.message}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Current Message */}
            <p className="text-sm text-white/70 font-medium text-center mt-2">
              {processingState.message}
            </p>
          </div>
        ) : (
          /* 결과 표시 (성공/실패) */
          <div className="flex flex-col items-center gap-4 w-full max-w-sm text-center">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center text-4xl ${processingState.result === 'success'
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-rose-500/20 text-rose-400'
              }`}>
              {processingState.result === 'success' ? '✓' : '✕'}
            </div>
            <p className={`text-lg font-black uppercase tracking-tight ${processingState.result === 'success' ? 'text-emerald-400' : 'text-rose-400'
              }`}>
              {processingState.result === 'success'
                ? (language === 'ko' ? '거래 완료' : 'Transaction Complete')
                : (language === 'ko' ? '거래 실패' : 'Transaction Failed')}
            </p>
            {processingState.resultMessage && (
              <p className="text-sm text-white/60 font-medium">
                {processingState.resultMessage}
              </p>
            )}
            {processingState.result === 'error' && (
              <button
                onClick={() => {
                  // 에러 상태 초기화 후 모달 닫기
                  onClose();
                }}
                className="mt-2 px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-black text-xs uppercase tracking-widest transition-all"
              >
                {language === 'ko' ? '닫기' : 'Close'}
              </button>
            )}
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          className="bg-[#0a0a0a] border border-white/10 p-8 rounded-3xl w-full max-w-lg space-y-6 shadow-2xl relative overflow-hidden"
        >
          {/* 진행 상태 오버레이 */}
          {renderProgressOverlay()}

          <div className="flex justify-between items-center">
            <h3 className="text-xl font-black italic uppercase tracking-tight flex items-center gap-2">
              {action === 'adjust' ? (
                <>
                  <Edit2 className="w-5 h-5 text-purple-500 animate-pulse" />
                  {t.adjustTitle}
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5 text-rose-500 animate-pulse" />
                  {t.liquidateTitle}
                </>
              )}
            </h3>
            <button
              onClick={onClose}
              disabled={processingState.isProcessing}
              className="p-2 hover:bg-white/5 rounded-full transition-colors disabled:opacity-20"
            >
              <X className="w-5 h-5 text-white/40" />
            </button>
          </div>

          <div className="space-y-5">
            {/* Asset Symbol Header Banner */}
            <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center font-bold text-purple-400">
                  {getHoldingDisplayName(holding, signal, language)[0]}
                </div>
                <div>
                  <div className="text-sm font-black text-white">{getHoldingDisplayName(holding, signal, language)}</div>
                  <div className="text-[10px] text-white/40 font-bold uppercase tracking-wider">{getHoldingSubText(holding, signal, language)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9px] text-white/30 uppercase font-black tracking-wider">
                  {language === 'ko' ? '현재 잔고' : 'Current Balance'}
                </div>
                <div className="text-sm font-mono font-bold text-white/80">
                  {holding.shares.toLocaleString()} SH
                </div>
              </div>
            </div>

            {/* Inputs Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] text-white/40 uppercase font-black tracking-widest mb-2 block">
                  {action === 'adjust' ? t.shares : t.sharesToLiq}
                </label>
                <input
                  type="number"
                  value={shares || ''}
                  onChange={e => {
                    const val = Number(e.target.value);
                    if (action === 'liquidate') {
                      setShares(Math.min(holding.shares, Math.max(0, val)));
                    } else {
                      setShares(Math.max(0, val));
                    }
                  }}
                  disabled={processingState.isProcessing}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 font-bold font-mono text-white outline-none focus:border-purple-500 transition-all text-lg disabled:opacity-30"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-[10px] text-white/40 uppercase font-black tracking-widest mb-2 block">
                  {action === 'adjust' ? t.avgCost : t.liqPrice}
                </label>
                <input
                  type="number"
                  value={priceOrAvgCost || ''}
                  onChange={e => setPriceOrAvgCost(Math.max(0, Number(e.target.value)))}
                  disabled={(isCash && action === 'adjust') || processingState.isProcessing}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 font-bold font-mono text-white outline-none focus:border-purple-500 transition-all text-lg disabled:opacity-30"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Transaction Fee input field */}
            <div>
              <label className="text-[10px] text-white/40 uppercase font-black tracking-widest mb-2 block">
                {t.fee} ({assetMarket})
              </label>
              <input
                type="number"
                value={fee || ''}
                onChange={e => setFee(Math.max(0, Number(e.target.value)))}
                disabled={processingState.isProcessing}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 font-bold font-mono text-white outline-none focus:border-purple-500 transition-all text-lg disabled:opacity-30"
                placeholder="0"
              />
            </div>

            {/* Dynamic Preview Banner for Liquidate */}
            {action === 'liquidate' && shares > 0 && !processingState.isProcessing && (
              <div className={`p-4 border rounded-2xl space-y-2 animate-in fade-in slide-in-from-top-2 ${netProfitLoss >= 0 ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' : 'bg-rose-500/5 border-rose-500/20 text-rose-400'
                }`}>
                <div className="flex justify-between items-center text-[10px] uppercase font-black tracking-wider opacity-60">
                  <span>{t.profitPreview}</span>
                  <span className="font-mono">{profitPct >= 0 ? '+' : ''}{profitPct.toFixed(2)}%</span>
                </div>
                <div className="text-2xl font-black tracking-tighter flex items-baseline gap-1">
                  {formatCurrency(netProfitLoss, assetMarket)}
                </div>
                <div className="text-[9px] opacity-40 font-bold leading-normal">
                  {language === 'ko'
                    ? `매도 대금 전액 ${formatCurrency(priceOrAvgCost * shares - fee, assetMarket)}이 현금 자산에 편입됩니다. (차익: ${formatCurrency(netProfitLoss, assetMarket)})`
                    : `Total proceeds of ${formatCurrency(priceOrAvgCost * shares - fee, assetMarket)} will be added to cash. (P&L: ${formatCurrency(netProfitLoss, assetMarket)})`
                  }
                  {shares >= holding.shares && ` ${t.liqWarning}`}
                </div>
              </div>
            )}

            {/* Dynamic Preview for Adjust Position */}
            {action === 'adjust' && shares > 0 && !processingState.isProcessing && (
              <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-2xl text-purple-400">
                <div className="text-[10px] uppercase font-black tracking-wider opacity-60 mb-1">
                  {language === 'ko' ? '조정 후 예상 총 가치' : 'Projected Post-Adjustment Value'}
                </div>
                <div className="text-xl font-black font-mono">
                  {formatCurrency(shares * priceOrAvgCost, assetMarket)}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              disabled={processingState.isProcessing}
              className="flex-1 px-6 py-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all font-black text-xs uppercase tracking-widest text-white/60 disabled:opacity-20"
            >
              {t.cancel}
            </button>
            <button
              onClick={handleConfirm}
              disabled={shares <= 0 || (action === 'liquidate' && shares > holding.shares) || processingState.isProcessing}
              className={`flex-1 px-6 py-4 rounded-xl transition-all font-black text-xs uppercase tracking-widest shadow-lg ${action === 'adjust'
                ? 'bg-purple-500 text-white hover:bg-purple-400 shadow-purple-500/20'
                : 'bg-rose-500 text-white hover:bg-rose-400 shadow-rose-500/20'
                } disabled:opacity-20 disabled:cursor-not-allowed`}
            >
              {processingState.isProcessing
                ? (language === 'ko' ? '처리 중...' : 'Processing...')
                : (action === 'adjust' ? t.confirmAdjust : t.confirmLiq)}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
